import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { portal } from '../portalApi.js'
import DateRange from '../components/DateRange.jsx'
import RichTextEditor from '../components/RichTextEditor.jsx'

// 업무관리 — 등록 한 번으로 오늘의 업무 → 상태변경 → 업무일지까지 이어진다.
// 팀원 화면은 "오늘 내가 무엇을 해야 하는가", 팀장 화면은 "오늘 내가 개입해야 하는 일이
// 무엇인가"에 먼저 답한다. 정상 진행 업무를 팀장이 일일이 보게 만들지 않는다.

// 요약 → 팀원 현황 → 목록이 한 장에 들어간다. 화면을 옮겨 다니며 같은 데이터를
// 다시 찾게 만들지 않는 게 요점이라, 예전의 오늘의 업무·전체 업무·캘린더를 '업무'로 합쳤다.
const TABS = [
  { k: 'main', label: '업무' },
  { k: 'dash', label: '기간 분석', lead: true },
  { k: 'journal', label: '업무일지' },
]
// 담당자를 색으로 가른다. 이름을 읽지 않아도 누구 일이 몰려 있는지 보이게 하려는 것이다.
// id 로 고르므로 사람이 늘어도 같은 사람은 늘 같은 색이다.
// 색각 이상까지 견디는지 검증기로 확인한 순서다 — 눈으로 고른 앞선 조합은 두 색이
// 회색으로 읽혔고 주황과 초록이 서로 구분되지 않았다. 색만으로 알아보게 하지도 않는다:
// 칸에 적히는 것은 업무명이고, 담당자는 칸에 마우스를 올리면 나온다.
const 담당자색 = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7']
const 색고르기 = (id) => 담당자색[(id || 0) % 담당자색.length]
// 사업부 색 — 회사에서 부르는 대로 분홍·보라·초록·주황이고, 베이비버스만 파랑이다.
// 처음에 노랑으로 뒀더니 옆자리 주황과 일반 시야에서도 ΔE 13.7 로 붙어 보여
// (하한 15) 검증에서 떨어졌다. 노랑을 밝히면 명도 상한을 넘고, 초록·빨강은 주황과
// 색각 이상에서 갈리지 않는다(ΔE 3.2 / 5.6). 파랑이 유일하게 전 항목을 통과했다.
// 보이는 차례대로 검증기를 돌렸다 — 이웃한 둘이 갈리는지가 요점이라 차례가 중요하다.
// 공통은 색을 주지 않는다: 회색을 끼우면 '채도 미달로 회색으로 읽힌다'며 떨어지고,
// 실제로도 공통은 어느 회사도 아니라는 뜻이라 색으로 가릴 것이 없다.
// 색만으로 알아보게 하지 않는다 — 옆에 회사 이름이 적히고 달력에는 범례를 둔다.
const 사업부색 = {
  ermore: '#e87ba4',      // 분홍
  linkmom_hq: '#4a3aa7',  // 보라
  babyplus: '#1baf7a',    // 초록
  linkmom: '#eb6834',     // 주황
  babybus: '#2a78d6',     // 파랑
}
// 달력은 일요일에서 시작한다 — 종이 달력과 같아야 눈이 헷갈리지 않는다.
// getDay() 가 일요일을 0 으로 주므로 그대로 쓰면 된다.
const WEEK_HEAD = ['일', '월', '화', '수', '목', '금', '토']
const STATUS_CLASS = {
  시작전: 'w-s-idle', 진행중: 'w-s-doing', 대기: 'w-s-hold',
  완료: 'w-s-done', 지연: 'w-s-late',
}
const TYPE_CLASS = { 협업: 'w-t-order', 요청: 'w-t-ask', 정기: 'w-t-cycle', 일반: 'w-t-plain' }
// 남에게 보내거나 같이 하는 업무 — 보낸 사람과 확인 시각이 남는다
const SENT = ['요청', '협업']
const TYPE_HINT = {
  일반: '내가 맡아서 할 일입니다. 담당자는 나로 고정됩니다.',
  요청: '남에게 맡깁니다. 팀장이 팀원에게 줄 때도 요청입니다. 받는 사람이 «확인»을 눌러야 전달이 끝나고, 보낸 사람과 확인 시각이 남습니다.',
  협업: '여럿이 같이 합니다. 담당자를 여러 명 고르세요 — 업무는 한 건으로 두고 사람만 여럿 붙습니다. 댓글과 이력은 같이 쌓입니다.',
}
const PCT = [0, 25, 50, 75, 100]

// 목록에서 바로 고칠 수 있는가. work.py 의 _owns 와 같은 조건이다 — 화면이 제 나름대로
// 정하면 칸은 열려 있는데 고르는 순간 403 이 난다. 협업자·참조자는 줄이 보이기만 한다.
const 고칠수있나 = (meta, i) =>
  !!meta.팀장 || i.지시자_id === meta.나.id
  || (i.담당자들 || [{ user_id: i.담당자_id }]).some((a) => a.user_id === meta.나.id)

// 붙일 수 있는 그림. work.py 의 «그림종류»와 같은 목록이다 — 화면에서 먼저 걸러야
// 10MB 를 올려 보내고 나서야 «안 된다»는 말을 듣지 않는다. SVG 는 뺀다(스크립트가 든다).
const 그림종류 = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

// 고르는 자리는 모두 같은 모양으로 둔다. 전에는 라디오 동그라미가 붙어 있었는데,
// 한 화면에 세 벌이 서니 «누르는 곳»보다 동그라미가 먼저 보였다. 모양(.seg)은 그대로
// 두고 라디오만 걷어낸다 — label 을 직접 누른다. 키보드로도 같게 쓰려고 role·tabIndex 를 준다.
function 고르기({ 값, 목록, onChange, 막힘 }) {
  return (
    <div className="seg">
      {목록.map(([k, l]) => {
        const 못고름 = !!막힘 && 막힘(k)   // 막힘을 안 넘기는 자리가 더 많다
        return (
          <label key={k} className={값 === k ? 'on' : ''} role="button"
            tabIndex={못고름 ? -1 : 0} aria-pressed={값 === k} aria-disabled={못고름}
            style={못고름 ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            onClick={() => !못고름 && onChange(k)}
            onKeyDown={(e) => {
              if (못고름 || !['Enter', ' '].includes(e.key)) return
              e.preventDefault(); onChange(k)
            }}>{l}</label>
        )
      })}
    </div>
  )
}

// 담당자 고르개에 뜰 사람. 팀원에게 남의 이름까지 보여 주면 고를 수 있는 것처럼
// 보이는데 서버는 막는다 — 보이는 것과 되는 것이 다르면 그게 고장으로 읽힌다.
const 고를사람 = (meta) =>
  (meta.팀장 ? meta.담당자 : meta.담당자.filter((u) => u.id === meta.나.id))

// 내 검토 차례인가 — 내가 확인자로 걸려 있고 업무가 검토대기에 서 있다.
// 확인자는 담당도 요청도 아닐 수 있어 고칠수있나로는 안 잡힌다(그래서 상태조차
// 못 바꾸고 멈춰 있었다). work.py 의 status_route 가 통과시키는 조건과 같다.
const 검토차례 = (meta, i) => i.검토자_id === meta.나.id && i.상태 === '검토대기'

// 지울 수 있는가. work.py 의 delete_route 와 같은 조건이다 — 화면이 제 나름대로 정하면
// 버튼은 보이는데 누르면 403 이 난다. 삭제는 되돌릴 수 없어 팀장만 한다.
const 지울수있나 = (meta) => !!meta.팀장

// 업무는 본사 DB 한 곳에 모여 있고 사업부 열로만 갈린다 — 회사를 전환해도 목록은 그대로다.
// 저장된 값은 회사 코드라 사람이 읽을 이름으로 바꿔 준다.
const 사업부이름 = (meta, code) =>
  (meta.사업부 || []).find((s) => s.code === code)?.name || code || '공통'

const monthStart = (m) => `${m}-01`
const monthEnd = (m) => {
  const [y, mm] = m.split('-').map(Number)
  return `${m}-${String(new Date(y, mm, 0).getDate()).padStart(2, '0')}`
}
const kstDate = (iso) => (iso ? new Date(iso).toLocaleString('sv-SE', { timeZone: 'Asia/Seoul', hour12: false }).slice(0, 16) : '')
const short = (d) => (d ? d.slice(5).replace('-', '/') : '')
const fileSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`)

function Badge({ kind, text }) {
  return <span className={`w-badge ${kind}`}>{text}</span>
}

// 대기로 바꿀 땐 사유와 예상 완료일을 반드시 받는다. 사유 없는 대기는 나중에 아무도
// 이유를 모르고, 그게 대기 장기화의 시작이다.
function HoldModal({ item, onClose, onSaved }) {
  const [reason, setReason] = useState(item.대기사유 || '')
  const [eta, setEta] = useState(item.예상완료일 || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const save = async () => {
    setBusy(true); setErr('')
    try {
      await portal.post(`/api/work/items/${item.id}/status`,
        { 상태: '대기', 대기사유: reason.trim(), 예상완료일: eta })
      onSaved(); onClose()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 460 }}>
        <div className="modal-header">
          <h3>대기 사유</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>{item.업무명}</div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 90 }}>대기사유</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="예: 거래처 자료 미회신" style={{ flex: 1 }} />
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 90 }}>예상 완료일</span>
            <input type="date" value={eta} onChange={(e) => setEta(e.target.value)} />
          </div>
          {err && <div className="error-box">{err}</div>}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={busy || !reason.trim() || !eta}
            onClick={save}>{busy ? '저장 중…' : '대기로 변경'}</button>
        </div>
      </div>
    </div>
  )
}

function WorkModal({ meta, item, onClose, onSaved }) {
  const isNew = !item?.id
  const [f, setF] = useState(() => ({
    // «지시»를 없앤 뒤에도 기본값이 그대로 남아, 팀장이 등록 창을 열면 아무 유형도
    // 안 골라진 채로 떴다(저장하면 400). 팀장은 대개 남에게 맡기므로 요청이 기본이다.
    유형: item?.유형 || (meta.팀장 ? '요청' : '일반'),
    업무명: item?.업무명 || '',
    // 한 줄 요약 칸은 없앴다 — 업무명이 곧 한 줄 요약이라 같은 말을 두 번 적게 됐다.
    // 값은 그대로 들고 다닌다: 예전에 적어 둔 것이 수정 한 번에 지워지면 안 된다.
    업무내용: item?.업무내용 || '',
    사업부: item?.사업부 || meta.기본사업부,
    카테고리: item?.카테고리 || '',
    담당자들: item?.담당자들?.length
      ? item.담당자들.map((a) => ({ id: a.user_id, name: a.name }))
      : [{ id: item?.담당자_id || meta.나.id, name: item?.담당자명 || meta.나.name }],
    시작일: item?.시작일 || meta.오늘,
    마감일: item?.마감일 || meta.오늘,
    마감시각: item?.마감시각 || '',
    우선순위: item?.우선순위 || '보통',
    팀장확인필요: !!item?.팀장확인필요,
    비고: item?.비고 || '',
    본문: item?.본문 || '',
    참조: item?.참조목록?.map((w) => w.user_id) || [],
    검토필요: !!item?.검토필요,
    검토자_id: item?.검토자_id || '',
  }))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // 등록할 때 할 일을 쪼갤 수 있어야 한다. 상세에서만 되면, 등록하고 다시 열어
  // 한 줄씩 넣게 되고 그러면 대개 안 넣는다.
  const [체크, set체크] = useState([])
  const [체크입력, set체크입력] = useState('')
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }))
  const 체크추가 = () => {
    const t = 체크입력.trim()
    if (!t) return
    set체크([...체크, t]); set체크입력('')
  }
  const types = meta.유형.filter((t) => t !== '정기')
  // 일반은 내 일이라 담당자가 나로 고정된다. 남에게 맡기려면 요청, 같이 하려면 협업.
  const 남에게 = SENT.includes(f.유형)
  // 담당자를 여럿 고를 수 있는 건 협업뿐이다. 요청은 받는 사람이 정해져 있고,
  // 일반은 내 일이다 — 아무 유형에나 여럿을 허용하면 «협업»이라는 갈래가 뜻을 잃는다.
  const 여럿 = f.유형 === '협업'
  useEffect(() => {
    if (!남에게) set('담당자들', [{ id: meta.나.id, name: meta.나.name }])
    else if (!여럿 && f.담당자들.length > 1) set('담당자들', f.담당자들.slice(0, 1))
  }, [f.유형])

  const save = async () => {
    setBusy(true); setErr('')
    try {
      if (isNew) {
        const r = await portal.post('/api/work/items', f)
        // 업무가 생긴 뒤에야 체크리스트를 붙일 수 있다 — 줄마다 업무 id 가 필요하다
        for (const 줄 of 체크) {
          await portal.post(`/api/work/items/${r.id}/checklist`, { 내용: 줄 })
        }
      } else await portal.post(`/api/work/items/${item.id}`, f)
      onSaved(); onClose()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 560 }}>
        <div className="modal-header">
          <h3>{isNew ? '업무 등록' : '업무 수정'}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* 등록한 뒤에도 유형을 고칠 수 있다 — 혼자 하려던 일을 나눠 맡게 되는 건
              흔하다. 정기 회차만 못 바꾼다: 템플릿에서 나온 이력이라 갈래를 바꾸면
              어느 템플릿의 몇 회차인지가 뜻을 잃는다. */}
          {f.유형 !== '정기' && (
            <div style={{ marginBottom: 12 }}>
              <고르기 값={f.유형} 목록={types.map((t) => [t, t])}
                onChange={(t) => set('유형', t)} />
            </div>
          )}
          {TYPE_HINT[f.유형] && (
            <div className="hint" style={{ marginBottom: 12 }}>{TYPE_HINT[f.유형]}</div>
          )}
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 78 }}>업무명</span>
            <input value={f.업무명} onChange={(e) => set('업무명', e.target.value)} style={{ flex: 1 }} />
          </div>
          <div className="row" style={{ marginBottom: 10, alignItems: 'flex-start' }}>
            <span className="muted" style={{ width: 78, paddingTop: 4 }}>담당자</span>
            <div style={{ flex: 1 }}>
              <div className="row" style={{ flexWrap: 'wrap', gap: 5 }}>
                {meta.담당자.map((u) => {
                  const 든다 = f.담당자들.some((x) => x.id === u.id)
                  return (
                    <button key={u.id} type="button" disabled={!남에게}
                      className={'data-tab' + (든다 ? ' active' : '')}
                      onClick={() => {
                        if (여럿) {
                          set('담당자들', 든다 ? f.담당자들.filter((x) => x.id !== u.id)
                            : [...f.담당자들, { id: u.id, name: u.name }])
                        } else set('담당자들', [{ id: u.id, name: u.name }])
                      }}>{u.name}</button>
                  )
                })}
              </div>
              <span className="muted" style={{ fontSize: 10 }}>
                {!남에게 ? '내 일이라 나로 고정됩니다'
                  : 여럿 ? '여러 명을 고를 수 있습니다 — 모두의 목록과 업무일지에 뜹니다'
                    : '한 명에게 맡깁니다'}
              </span>
            </div>
            <span className="muted" style={{ width: 60, marginLeft: 10, paddingTop: 4 }}>우선순위</span>
            <select value={f.우선순위} onChange={(e) => set('우선순위', e.target.value)}>
              {meta.우선순위.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 78 }}>사업부</span>
            <select value={f.사업부} onChange={(e) => set('사업부', e.target.value)}>
              {meta.사업부.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
            <span className="muted" style={{ width: 60, marginLeft: 10 }}>갈래</span>
            <select value={f.카테고리} onChange={(e) => set('카테고리', e.target.value)}>
              <option value="">지정 안 함</option>
              {(meta.카테고리 || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="hint" style={{ marginBottom: 10 }}>
            사업부는 어느 회사 일인지입니다 — 회사를 전환해도 업무 목록은 그대로입니다.
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 78 }}>기간</span>
            <input type="date" value={f.시작일} onChange={(e) => set('시작일', e.target.value)} />
            <span className="muted">~</span>
            <input type="date" value={f.마감일} onChange={(e) => set('마감일', e.target.value)} />
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 78 }}>마감시각</span>
            <input type="time" value={f.마감시각 || ''} style={{ width: 130 }}
              onChange={(e) => set('마감시각', e.target.value)} />
            <span className="muted" style={{ fontSize: 12 }}>
              비워 두면 날짜만 봅니다 (예: 자금일보 10:30)
            </span>
          </div>

          {/* 같이 하는 사람은 담당자에 든다. 여기 남는 것은 «알고만 있는 사람»뿐이다 —
              둘을 한 칸에 몰면 누구에게 손을 빌릴 수 있는지가 안 보인다. */}
          <div className="row" style={{ marginBottom: 8, alignItems: 'flex-start' }}>
            <span className="muted" style={{ width: 78, paddingTop: 4 }}>참조</span>
            <div style={{ flex: 1 }}>
              <div className="row" style={{ flexWrap: 'wrap', gap: 5 }}>
                {meta.담당자.filter((u) => !f.담당자들.some((x) => x.id === u.id)).map((u) => (
                  <button key={u.id} type="button"
                    className={'data-tab' + (f.참조.includes(u.id) ? ' active' : '')}
                    onClick={() => set('참조', f.참조.includes(u.id)
                      ? f.참조.filter((x) => x !== u.id) : [...f.참조, u.id])}>{u.name}</button>
                ))}
              </div>
              <span className="muted" style={{ fontSize: 10 }}>
                알고만 있습니다 — 목록에는 뜨지만 고칠 수는 없습니다
              </span>
            </div>
          </div>

          {/* 검토 — 끝냈다고 바로 닫히면 안 되는 일이 있다 */}
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 78 }}>검토</span>
            <label className="row" style={{ gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={f.검토필요}
                onChange={(e) => set('검토필요', e.target.checked)} />
              <span>검토 대상</span>
            </label>
            {f.검토필요 && (
              <>
                <span className="muted" style={{ marginLeft: 8 }}>확인자</span>
                <select value={f.검토자_id}
                  onChange={(e) => {
                    const id = Number(e.target.value) || ''
                    set('검토자_id', id)
                    set('검토자명', meta.담당자.find((u) => u.id === id)?.name || '')
                  }}>
                  <option value="">고르세요</option>
                  {meta.담당자.filter((u) => !f.담당자들.some((x) => x.id === u.id)).map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <span className="muted" style={{ fontSize: 10, marginLeft: 6 }}>
                  담당자는 검토대기까지만, 완료는 확인자가 합니다
                </span>
              </>
            )}
          </div>
          {isNew && (
            <div style={{ marginBottom: 10 }}>
              <div className="muted" style={{ marginBottom: 4 }}>
                체크리스트 — 할 일을 쪼개 둡니다 (등록 뒤에도 상세에서 더할 수 있습니다)
              </div>
              {체크.map((줄, k) => (
                <div key={k} className="row" style={{ gap: 6, marginBottom: 3 }}>
                  <span style={{ flex: 1, fontSize: 13 }}>· {줄}</span>
                  <button type="button" className="link-cell"
                    onClick={() => set체크(체크.filter((_, j) => j !== k))}>빼기</button>
                </div>
              ))}
              <div className="row" style={{ gap: 6 }}>
                <input value={체크입력} style={{ flex: 1 }} placeholder="할 일 한 줄 추가"
                  onChange={(e) => set체크입력(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); 체크추가() } }} />
                <button type="button" className="btn secondary" onClick={체크추가}>추가</button>
              </div>
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            <div className="muted" style={{ marginBottom: 4 }}>본문 — 표나 화면 캡처를 붙여넣어도 됩니다</div>
            <RichTextEditor value={f.본문} onChange={(v) => set('본문', v)} minHeight={160} />
          </div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="muted" style={{ width: 78 }}>비고</span>
            <input value={f.비고} onChange={(e) => set('비고', e.target.value)} style={{ flex: 1 }} />
          </div>
          <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.팀장확인필요}
              onChange={(e) => set('팀장확인필요', e.target.checked)} />
            <span>팀장 확인 필요</span>
            {/* «검토 대상»과 헷갈리기 쉬워 차이를 적어 둔다 — 이쪽은 완료를 막지 않는다. */}
            <span className="muted" style={{ fontSize: 12 }}>
              — 팀장 화면 예외 목록에 올라갑니다 (완료는 담당자가 합니다)
            </span>
          </label>
          {err && <div className="error-box" style={{ marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={busy || !f.업무명.trim()}
            onClick={save}>{busy ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  )
}

// 상세는 페이지다 — 제목·속성·본문·첨부·댓글이 한 자리에 있어야 노션에서 하던 일이 여기서 끝난다.
// 작은 모달에 이력만 띄우면 결국 설명은 메신저로, 파일은 메일로 새 나간다.
function DetailPage({ id, meta, onClose, onChanged, onEdit }) {
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [editBody, setEditBody] = useState(false)
  const [body, setBody] = useState('')
  const [check, setCheck] = useState('')
  const [붙일그림, set붙일그림] = useState([])
  const fileRef = useRef(null)

  // 노션처럼 — 캡처를 찍어 Ctrl+V. 클립보드에 들어온 그림만 집는다(글은 그대로 붙게 둔다).
  const 그림담기 = (files) => {
    const 그림 = [...files].filter((f) => 그림종류.includes(f.type))
    if (!그림.length) return false
    set붙일그림((s) => [...s, ...그림.map((f) => ({ file: f, url: URL.createObjectURL(f) }))])
    return true
  }
  const 그림붙이기 = (e) => {
    if (그림담기([...(e.clipboardData?.files || [])])) e.preventDefault()
  }
  const 그림떨구기 = (e) => {
    if (그림담기([...(e.dataTransfer?.files || [])])) e.preventDefault()
  }
  // 창을 닫을 때 미리보기로 만들어 둔 주소를 놓아 준다(안 놓으면 브라우저가 붙들고 있다)
  useEffect(() => () => 붙일그림.forEach((g) => URL.revokeObjectURL(g.url)), [])

  const load = useCallback(() => {
    portal.get(`/api/work/items/${id}`)
      .then((r) => { setD(r); setBody(r.item.본문 || '') })
      .catch((e) => setErr(e.message))
  }, [id])
  useEffect(() => { load() }, [load])

  const act = async (fn) => {
    setBusy(true); setErr('')
    try { await fn(); load(); onChanged?.() } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const upload = async (files) => {
    if (!files?.length) return
    await act(() => portal.upload(`/api/work/items/${id}/files`, {}, files))
  }

  if (err && !d) return <div className="error-box">{err}</div>
  if (!d) return <div className="muted">불러오는 중…</div>

  const it = d.item
  const 내가담당 = (d.담당자들 || []).some((a) => a.user_id === meta.나.id)
    || it.담당자_id === meta.나.id
  const 내가요청 = it.지시자_id === meta.나.id
  const 보낸업무 = SENT.includes(it.유형)

  return (
    <>
      {/* 내 검토 차례면 제일 위에 띄운다 — 아래로 내려가면 «내가 뭘 해야 하는지»가
          본문에 묻힌다. 검토 완료를 누르면 그대로 완료로 닫힌다(따로 상태를 또 바꾸지
          않는다 — 두 번 눌러야 끝나면 한 번만 누르고 끝난 줄 안다). */}
      {검토차례(meta, it) && (
        <div className="row" style={{ gap: 8, marginBottom: 8, padding: '7px 10px',
          border: `1px solid ${상태색.검토대기}`, borderRadius: 6, background: '#fffaf0' }}>
          <b style={{ color: 상태색.검토대기 }}>내 검토 차례</b>
          <span className="muted" style={{ fontSize: 12 }}>
            {it.담당자명}님이 끝내고 확인을 기다립니다
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn" disabled={busy}
            onClick={() => act(() => portal.post(`/api/work/items/${id}/status`, { 상태: '완료' }))}>
            검토 완료 — 완료로 닫기
          </button>
          <button type="button" className="btn secondary" disabled={busy}
            onClick={() => act(() => portal.post(`/api/work/items/${id}/status`, { 상태: '진행중' }))}>
            돌려보내기
          </button>
        </div>
      )}
      {/* 어느 회사 무슨 갈래 일인지는 창을 열자마자 보여야 한다 — 목록에서는 제목
          왼쪽에 서 있던 것이 상세로 들어오면 사라져, 확인하려면 창을 닫고 목록을
          다시 봐야 했다. 속성 표 안에 한 줄로 넣으면 여느 줄과 섞여 안 보인다.
          색은 목록·달력과 같은 것을 쓰되 옅게 깔고 왼쪽 막대로만 진하게 둔다 —
          색 위에 흰 글씨를 얹으면 옅은 색(베이비플러스 초록·에르모어 분홍)에서 안 읽힌다. */}
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
        <span className="row" style={{
          gap: 8, padding: '5px 11px', borderRadius: 5, minWidth: 0,
          borderLeft: `4px solid ${사업부색[it.사업부] || '#cbd5e1'}`,
          background: `${사업부색[it.사업부] || '#cbd5e1'}1a`,
        }}>
          <b style={{ fontSize: 13 }}>{사업부이름(meta, it.사업부)}</b>
          <span style={{ color: '#c9cfd8' }}>|</span>
          <span style={{ fontSize: 13 }}>
            {it.카테고리 || <span className="muted">갈래 없음</span>}
          </span>
        </span>
        <span>
          {보낸업무 && 내가요청 && it.상태 === '완료' && !it.요청자확인일시 && (
            <button type="button" className="btn" disabled={busy}
              onClick={() => act(() => portal.post(`/api/work/items/${id}/confirm`))}>
              결과 확인
            </button>
          )}
          {보낸업무 && !it.확인일시 && 내가담당 && (
            <button type="button" className="btn" disabled={busy}
              onClick={() => act(() => portal.post(`/api/work/items/${id}/ack`))}>확인</button>
          )}
          {/* 등록할 때 고른 것을 나중에 못 고치면, 잘못 고른 채로 그냥 두게 된다.
              참조·유형·팀장 확인처럼 상세에 칸이 없는 것들이 특히 그렇다. */}
          {고칠수있나(meta, { ...it, 담당자들: d.담당자들 }) && (
            <button type="button" className="btn secondary" disabled={busy}
              style={{ marginLeft: 6 }}
              onClick={() => onEdit?.({ ...it, 담당자들: d.담당자들, 참조목록: d.watchers })}>
              수정
            </button>
          )}
          {지울수있나(meta) && (
            <button type="button" className="btn secondary" disabled={busy}
              style={{ marginLeft: 6 }}
              onClick={async () => {
                if (!window.confirm(`«${it.업무명}» 을(를) 지웁니다.\n댓글·첨부·이력도 함께 사라지고 되돌릴 수 없습니다.`)) return
                setBusy(true); setErr('')
                try { await portal.del(`/api/work/items/${id}`); onChanged?.(); onClose() }
                catch (e) { setErr(e.message); setBusy(false) }
              }}>삭제</button>
          )}
        </span>
      </div>

      <div className="panel">
        <h2 className="w-title">{it.업무명}</h2>
        <table className="w-props">
          <tbody>
            <tr><th>유형</th><td><Badge kind={TYPE_CLASS[it.유형]} text={it.유형} /></td></tr>
            <tr><th>{보낸업무 ? (it.유형 === '요청' ? '요청자' : '지시자') : '등록자'}</th>
              <td>{it.지시자명 || it.created_by_name || '—'}
                {it.지시일시 && <span className="muted"> · {kstDate(it.지시일시)}</span>}</td></tr>
            <tr><th>담당자</th>
              <td className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {(d.담당자들 || []).length
                  ? <span>{d.담당자들.map((a) => a.name).join(', ')}</span>
                  : <span className="muted">아직 맡은 사람이 없습니다</span>}
                {/* 고르개 하나로 끝낸다 — 팀원에게는 자기 이름만 떠서 «내가 맡기»와
                    같은 일이 되고, 팀장은 같은 자리에서 남을 세우거나 미지정으로 되돌린다.
                    담당자가 여럿인 업무는 고르개 하나로 뭉개지므로 «수정»으로 보낸다. */}
                {!['완료', '취소'].includes(it.상태) && (d.담당자들 || []).length <= 1
                  && (meta.팀장 || !(d.담당자들 || []).length) && (
                  <select className="w-inline" disabled={busy}
                    value={(d.담당자들 || [])[0]?.user_id || ''}
                    onChange={(e) => act(() => (meta.팀장
                      ? portal.post(`/api/work/items/${id}`,
                        { 담당자들: e.target.value ? [{ id: Number(e.target.value) }] : [] })
                      : portal.post(`/api/work/items/${id}/claim`)))}>
                    <option value="">
                      {(d.담당자들 || []).length ? '미지정으로 되돌리기' : '담당자 지정…'}
                    </option>
                    {고를사람(meta).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                )}
                {meta.팀장 && (d.담당자들 || []).length > 1 && (
                  <span className="muted" style={{ fontSize: 11 }}>
                    여러 명 — «수정»에서 바꿉니다
                  </span>
                )}
              </td></tr>
            <tr><th>상태</th>
              <td><Badge kind={STATUS_CLASS[it.상태]} text={it.상태} />
                <span className="muted"> · 진행률 {it.진행률}%</span>
                {it.지연일수 > 0 && <span className="w-late"> · {it.지연일수}일 지연</span>}</td></tr>
            <tr><th>기간</th><td>{it.시작일} ~ {it.마감일}{it.마감시각 ? ` ${it.마감시각}` : ''}</td></tr>
            <tr><th>담당자 확인</th>
              <td>{it.확인일시 ? kstDate(it.확인일시) : <span className="muted">아직 확인 전</span>}</td></tr>
            {보낸업무 && (
              <tr><th>요청자 확인</th>
                <td>{it.요청자확인일시 ? kstDate(it.요청자확인일시)
                  : <span className="muted">{it.상태 === '완료' ? '완료됐지만 아직 확인 전' : '—'}</span>}</td></tr>
            )}
            <tr><th>참조</th>
              <td>{d.watchers.length
                ? d.watchers.map((w) => <span key={w.user_id} className="perm-chip">{w.name}</span>)
                : <span className="muted">—</span>}</td></tr>
            {it.상태 === '대기' && (
              <tr><th>대기</th><td>{it.대기사유} <span className="muted">· 예상 완료 {it.예상완료일}</span></td></tr>
            )}
            {it.비고 && <tr><th>비고</th><td>{it.비고}</td></tr>}
          </tbody>
        </table>

        {it.업무내용 && <div className="hint" style={{ marginTop: 10 }}>{it.업무내용}</div>}

        <div className="row" style={{ justifyContent: 'space-between', margin: '16px 0 6px' }}>
          <b>본문</b>
          {!editBody && (
            <button type="button" className="link-cell" onClick={() => setEditBody(true)}>편집</button>
          )}
        </div>
        {editBody ? (
          <>
            <RichTextEditor value={body} onChange={setBody} minHeight={220}
              placeholder="요청 내용을 적으세요. 표나 화면 캡처를 붙여넣어도 됩니다." />
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn secondary"
                onClick={() => { setBody(it.본문 || ''); setEditBody(false) }}>취소</button>
              <button type="button" className="btn" disabled={busy}
                onClick={() => act(async () => {
                  await portal.post(`/api/work/items/${id}`, { 본문: body })
                  setEditBody(false)
                })}>본문 저장</button>
            </div>
          </>
        ) : it.본문 ? (
          <div className="doc-body w-body" dangerouslySetInnerHTML={{ __html: it.본문 }} />
        ) : (
          <div className="muted">본문이 없습니다. 편집을 눌러 적으세요.</div>
        )}

        <div className="row" style={{ justifyContent: 'space-between', margin: '16px 0 6px' }}>
          <b>첨부</b>
          <button type="button" className="link-cell" onClick={() => fileRef.current?.click()}>
            파일 올리기
          </button>
        </div>
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
          onChange={(e) => { upload([...e.target.files]); e.target.value = '' }} />
        <div className="w-files"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]) }}>
          {d.files.map((f) => (
            <div key={f.id} className="w-file">
              <button type="button" className="link-cell"
                onClick={() => portal.download(`/api/work/files/${f.id}`)}>{f.파일명}</button>
              <span className="muted">{fileSize(f.크기)} · {f.올린이}</span>
              <button type="button" className="link-cell" style={{ color: 'var(--bad)' }}
                onClick={() => act(() => portal.del(`/api/work/files/${f.id}`))}>삭제</button>
            </div>
          ))}
          {!d.files.length && <span className="muted">여기로 파일을 끌어다 놓아도 됩니다 · 파일당 10MB</span>}
        </div>

        {/* 체크리스트 — 업무 하나가 여러 손질로 이뤄질 때, 어디까지 했는지를 댓글로
            적으면 다음 사람이 처음부터 읽어야 한다. */}
        <b style={{ display: 'block', margin: '16px 0 6px' }}>
          체크리스트
          {(d.checklist || []).length > 0 && (
            <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
              {d.checklist.filter((c) => c.완료).length} / {d.checklist.length}
            </span>
          )}
        </b>
        {(d.checklist || []).map((c) => (
          <label key={c.id} className="row" style={{ gap: 6, cursor: 'pointer', marginBottom: 2 }}>
            <input type="checkbox" checked={c.완료} disabled={busy}
              onChange={(e) => act(() => portal.post(`/api/work/checklist/${c.id}`,
                { 완료: e.target.checked }))} />
            <span style={{ textDecoration: c.완료 ? 'line-through' : 'none',
                           color: c.완료 ? 'var(--muted)' : undefined }}>{c.내용}</span>
            <button type="button" className="link-cell" style={{ marginLeft: 'auto' }}
              onClick={() => act(() => portal.del(`/api/work/checklist/${c.id}`))}>지우기</button>
          </label>
        ))}
        {!(d.checklist || []).length && <div className="muted">아직 없습니다.</div>}
        <div className="row" style={{ gap: 6, marginTop: 4 }}>
          <input placeholder="할 일 한 줄 추가" value={check} style={{ flex: 1 }}
            onChange={(e) => setCheck(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && check.trim()) {
                act(() => portal.post(`/api/work/items/${id}/checklist`, { 내용: check }))
                setCheck('')
              }
            }} />
          <button type="button" className="btn secondary" disabled={busy || !check.trim()}
            onClick={() => { act(() => portal.post(`/api/work/items/${id}/checklist`, { 내용: check })); setCheck('') }}>
            추가
          </button>
        </div>

        {/* 마감일을 몇 번 밀었는지가 그 업무의 신호다 — 이력에 한 줄로 묻어 두지 않는다 */}
        {(d.마감변경 || []).length > 0 && (
          <>
            <b style={{ display: 'block', margin: '16px 0 6px' }}>
              마감일 변경 <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>
                {d.마감변경.length}번 바뀜</span>
            </b>
            <table className="w-history">
              <tbody>
                {d.마감변경.map((c) => (
                  <tr key={c.id}>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>{kstDate(c.시각)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{c.바꾼이명 || '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.이전마감일 || '미지정'} → <b>{c.새마감일 || '미지정'}</b>
                    </td>
                    <td className="l muted">{c.사유 || '사유 없음'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <b style={{ display: 'block', margin: '16px 0 6px' }}>댓글</b>
        {d.comments.map((c) => (
          <div key={c.id} className="w-comment">
            <div><b>{c.작성자명}</b> <span className="muted">{kstDate(c.작성시각)}</span></div>
            {c.내용 && <div style={{ whiteSpace: 'pre-wrap' }}>{c.내용}</div>}
            {/* 캡처는 줄여서 그 자리에 보여 준다 — 누르면 원래 크기로 새 창에 뜬다.
                «내려받아서 열어 보세요»로 두면 아무도 안 연다. */}
            {(c.그림 || []).length > 0 && (
              <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                {c.그림.map((g) => (
                  <a key={g.id} href={`/api/work/files/${g.id}?inline=1`}
                    target="_blank" rel="noopener noreferrer" title={g.파일명}>
                    <img src={`/api/work/files/${g.id}?inline=1`} alt={g.파일명}
                      style={{ maxHeight: 190, maxWidth: '100%', borderRadius: 5,
                               border: '1px solid #e5e9ef', display: 'block' }} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {!d.comments.length && <div className="muted">아직 댓글이 없습니다.</div>}
        {붙일그림.length > 0 && (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {붙일그림.map((g, n) => (
              <span key={g.url} style={{ position: 'relative', display: 'inline-block' }}>
                <img src={g.url} alt={g.file.name}
                  style={{ maxHeight: 90, borderRadius: 5, border: '1px solid #e5e9ef',
                           display: 'block' }} />
                {/* 잘못 붙였을 때 되돌릴 자리가 없으면 창을 닫았다 다시 열게 된다 */}
                <button type="button" className="btn tiny secondary"
                  style={{ position: 'absolute', top: 2, right: 2, padding: '0 5px' }}
                  onClick={() => set붙일그림(붙일그림.filter((_, i) => i !== n))}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="row" style={{ marginTop: 8, alignItems: 'flex-start' }}>
          <textarea rows={2} value={comment} style={{ flex: 1 }}
            placeholder="모르는 게 있으면 여기서 물어보세요 — 캡처는 Ctrl+V 로 붙여넣으면 됩니다"
            onPaste={그림붙이기} onDrop={그림떨구기} onDragOver={(e) => e.preventDefault()}
            onChange={(e) => setComment(e.target.value)} />
          <button type="button" className="btn"
            disabled={busy || (!comment.trim() && !붙일그림.length)}
            onClick={() => act(async () => {
              // 글만이면 예전처럼 JSON, 그림이 붙었으면 한 번에 같이 올린다 —
              // 따로 올리면 올리다 만 그림이 주인 없이 남는다.
              if (붙일그림.length) {
                await portal.upload(`/api/work/items/${id}/comments`,
                  { 내용: comment }, 붙일그림.map((g) => g.file))
                붙일그림.forEach((g) => URL.revokeObjectURL(g.url))
                set붙일그림([])
              } else {
                await portal.post(`/api/work/items/${id}/comments`, { 내용: comment })
              }
              setComment('')
            })}>등록</button>
        </div>
        <div className="hint" style={{ marginTop: 4 }}>
          캡처는 Ctrl+V 로 붙여넣거나 여기에 끌어다 놓으세요 · PNG·JPG·GIF·WEBP · 장당 10MB
        </div>

        <b style={{ display: 'block', margin: '16px 0 6px' }}>이력</b>
        <div className="hint" style={{ marginBottom: 8 }}>
          누가 잘했나 보려는 기록이 아니라, 업무가 어디서 멈췄는지 찾기 위한 기록입니다.
        </div>
        <table className="w-history">
          <tbody>
            {d.history.map((h) => (
              <tr key={h.id}>
                <td className="muted" style={{ whiteSpace: 'nowrap' }}>{kstDate(h.시각)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{h.사용자명 || '-'}</td>
                <td className="muted">{h.종류}</td>
                <td className="l">{h.내용}</td>
              </tr>
            ))}
            {!d.history.length && <tr><td className="muted c">기록 없음</td></tr>}
          </tbody>
        </table>
        {err && <div className="error-box" style={{ marginTop: 10 }}>{err}</div>}
      </div>
    </>
  )
}

function RecurringModal({ meta, onClose, onSaved }) {
  const [list, setList] = useState([])
  const 빈폼 = () => ({
    id: null,
    업무명: '', 담당자들: [{ id: meta.나.id, name: meta.나.name }], 주기: '매일', 시작기준: 1,
    마감오프셋: 0, 우선순위: '보통', 업무내용: '', 사용여부: true,
    사업부: meta.기본사업부, 시작월: 1, 말일여부: false, 카테고리: '',
    휴일정책: '그대로',
  })
  const [f, setF] = useState(빈폼)
  // 무엇을 물어야 하는지는 서버가 정한다 — 화면이 따로 판단하면 저장할 때 어긋난다
  const 월필요 = (meta.월지정주기 || []).includes(f.주기)
  const 일필요 = (meta.일지정주기 || []).includes(f.주기)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [made, setMade] = useState(null)
  const load = useCallback(() => {
    portal.get('/api/work/recurring').then(setList).catch((e) => setErr(e.message))
  }, [])
  useEffect(() => { load() }, [load])
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }))
  const save = async () => {
    setBusy(true); setErr(''); setMade(null)
    try {
      const r = await portal.post('/api/work/recurring', f)
      setMade(r.다시만듦 ? { 생성: r.생성, 다시: r.다시만듦 } : r.생성)
      setF(빈폼())
      load(); onSaved()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  // 템플릿을 고친다. 이미 만들어진 회차는 손대지 않는다 — 지나간 회차의 마감일이
  // 뒤늦게 바뀌면 그때 왜 그렇게 했는지가 어긋난다. 앞으로 생길 회차부터 새 설정을
  // 따르고, 아직 오지 않은 회차까지 다시 만들려면 «이후 회차» 를 누른다.
  const 고치기 = (t) => {
    setErr(''); setMade(null)
    setF({
      id: t.id,
      업무명: t.업무명 || '',
      // 미지정 템플릿은 담당자가 아예 없다 — 옛 한 명짜리 값을 그대로 붙이면
      // {id: null} 이 들어가 «이름 없는 담당자»가 하나 생긴다.
      담당자들: (t.담당자들 || []).length
        ? t.담당자들.map((a) => ({ id: a.user_id, name: a.name }))
        : (t.담당자_id ? [{ id: t.담당자_id, name: t.담당자명 }] : []),
      주기: t.주기, 시작기준: t.시작기준 || 1, 마감오프셋: t.마감오프셋 || 0,
      우선순위: t.우선순위 || '보통', 업무내용: t.업무내용 || '',
      사용여부: !!t.사용여부, 사업부: t.사업부 || meta.기본사업부,
      시작월: t.시작월 || 1, 말일여부: !!t.말일여부, 카테고리: t.카테고리 || '',
      휴일정책: t.휴일정책 || '그대로',
    })
  }

  const remove = async (id) => {
    try { await portal.del(`/api/work/recurring/${id}`); load() }
    catch (e) { setErr(e.message) }
  }
  const 기준설명 = (t) => {
    if (t.주기 === '매일') return '평일 매일'
    if (t.주기 === '매주') return `매주 ${meta.요일[(t.시작기준 || 1) - 1]}요일`
    const 날 = t.말일여부 ? '말일' : `${t.시작기준}일`
    if (t.주기 === '매월') return `매월 ${날}`
    if (t.주기 === '매년') return `매년 ${t.시작월}월 ${날}`
    return `${t.주기} ${t.시작월}번째 달 ${날}`
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 760 }}>
        <div className="modal-header">
          <h3>정기업무 설정</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="hint" style={{ marginBottom: 12 }}>
            템플릿은 완료 대상이 아닙니다. 날짜가 되면 그날치 업무 건이 따로 생기고,
            매월 같은 업무라도 완료 여부와 이력이 각각 남습니다.
            매일 반복은 주말을 건너뜁니다 — 안 할 일을 지연으로 쌓지 않기 위해서입니다.
            {!meta.팀장 && ' 내가 맡은 정기업무만 보이고, 등록도 내 앞으로만 됩니다 — 남에게 걸려면 팀장에게 요청하세요.'}
            마감이 주말·공휴일에 걸리면 «휴일 처리» 대로 옮깁니다. 고쳐서 저장하면
            아직 오지 않은 회차는 새 설정으로 저절로 다시 만들어집니다.
          </div>
          <div className="row" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <input placeholder="업무명" value={f.업무명} style={{ width: 200 }}
              onChange={(e) => set('업무명', e.target.value)} />
            {/* 정기업무도 여럿이 나눠 맡는다(월 결산 같은 것). 고른 사람 모두가
                회차마다 담당자가 된다 — 회차를 사람 수만큼 따로 만들지 않는다. */}
            <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
              {/* 매달 맡는 사람이 바뀌는 일이 있다. 사람을 박아 두면 매달 템플릿을 고쳐야
                  하고, 안 고친 달에는 떠난 사람 앞으로 계속 쌓인다. 미지정으로 걸어 두면
                  그 달에 맡을 사람이 회차를 가져간다. 팀 전체에 열어 두는 것이라 팀장만. */}
              {meta.팀장 && (
                <button type="button"
                  className={'data-tab' + (f.담당자들.length === 0 ? ' active' : '')}
                  title="그 달에 맡을 사람이 목록에서 자기 이름을 골라 가져갑니다"
                  onClick={() => set('담당자들', [])}>담당자 미지정</button>
              )}
              {(meta.팀장 ? meta.담당자 : meta.담당자.filter((u) => u.id === meta.나.id))
                .map((u) => {
                  const 든다 = f.담당자들.some((x) => x.id === u.id)
                  return (
                    <button key={u.id} type="button"
                      className={'data-tab' + (든다 ? ' active' : '')}
                      onClick={() => set('담당자들', 든다
                        ? f.담당자들.filter((x) => x.id !== u.id)
                        : [...f.담당자들, { id: u.id, name: u.name }])}>{u.name}</button>
                  )
                })}
            </span>
            <select value={f.사업부} onChange={(e) => set('사업부', e.target.value)} title="사업부">
              {meta.사업부.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
            <select value={f.카테고리} onChange={(e) => set('카테고리', e.target.value)} title="갈래">
              <option value="">갈래 없음</option>
              {(meta.카테고리 || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={f.주기} onChange={(e) => set('주기', e.target.value)}>
              {meta.주기.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {f.주기 === '매주' && (
              <select value={f.시작기준} onChange={(e) => set('시작기준', Number(e.target.value))}>
                {meta.요일.map((w, i) => <option key={w} value={i + 1}>{w}요일</option>)}
              </select>
            )}
            {월필요 && (
              <>
                <select value={f.시작월} onChange={(e) => set('시작월', Number(e.target.value))}
                  title={`${f.주기} 안에서 몇 번째 달`}>
                  {Array.from({ length: (meta.월범위 || {})[f.주기] || 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {f.주기 === '매년' ? `${i + 1}월` : `${i + 1}번째 달`}
                    </option>
                  ))}
                </select>
              </>
            )}
            {일필요 && (
              <>
                <label className="row" style={{ gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.말일여부}
                    onChange={(e) => set('말일여부', e.target.checked)} />
                  <span className="muted">말일</span>
                </label>
                {!f.말일여부 && (
                  <>
                    <input type="number" min={1} max={31} style={{ width: 70 }} value={f.시작기준}
                      onChange={(e) => set('시작기준', Number(e.target.value))} title="시작일" />
                    <span className="muted">일</span>
                  </>
                )}
              </>
            )}
            <span className="muted">소요</span>
            <input type="number" min={0} max={60} style={{ width: 70 }} value={f.마감오프셋}
              onChange={(e) => set('마감오프셋', Number(e.target.value))} title="시작일부터 마감까지 며칠" />
            <span className="muted">일</span>
            <select value={f.휴일정책} onChange={(e) => set('휴일정책', e.target.value)}
              title="마감이 주말·공휴일에 걸렸을 때">
              <option value="그대로">휴일이어도 그대로</option>
              <option value="이전영업일">휴일이면 앞 영업일로</option>
              <option value="다음영업일">휴일이면 다음 영업일로</option>
            </select>
            <button type="button" className="btn" disabled={busy || !f.업무명.trim()}
              onClick={save}>{busy ? '저장 중…' : f.id ? '고친 내용 저장' : '추가'}</button>
            {f.id && (
              <button type="button" className="btn secondary" onClick={() => setF(빈폼())}>
                그만두기
              </button>
            )}
          </div>
          {f.id && (
            <div className="hint" style={{ marginBottom: 10 }}>
              «{list.find((t) => t.id === f.id)?.업무명}» 을 고치는 중입니다.
              저장하면 <b>아직 오지 않은 회차가 새 설정으로 다시 만들어집니다.</b>
              지나간 회차와 이미 손댄 회차(상태를 바꿨거나 본문·댓글·체크리스트·첨부를
              남긴 것)는 그대로 둡니다 — 과거 기록과 남이 적어 둔 것은 지우지 않습니다.
            </div>
          )}

          {err && <div className="error-box">{err}</div>}
          {made != null && (
            <div className="hint">
              {typeof made === 'object' ? (
                <>
                  저장했습니다. 아직 오지 않은 회차 {made.다시}건을 새 설정으로 다시 만들었고,
                  업무 {made.생성}건이 생겼습니다. 지나간 회차와 손댄 회차는 그대로입니다.
                </>
              ) : (
                <>
                  저장했습니다. 업무 {made}건을 만들었습니다
                  {made === 0 && ' — 아직 해당 날짜가 오지 않았습니다. 아래 «다음 생성»을 보세요'}.
                </>
              )}
            </div>
          )}
          <table style={{ marginTop: 10 }}>
            <thead>
              <tr><th className="l">업무명</th><th>사업부</th><th>갈래</th><th>담당자</th><th>반복</th><th>마감</th><th>휴일 처리</th><th>다음 생성</th><th>사용</th><th /></tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className={f.id === t.id ? 'on' : ''}>
                  <td className="l">{t.업무명}</td>
                  <td className="c">{사업부이름(meta, t.사업부)}</td>
                  <td className="c">{t.카테고리 || '—'}</td>
                  <td className="c" title={(t.담당자들 || []).map((a) => a.name).join(', ')}>
                    {t.담당자명}
                    {(t.담당자들 || []).length > 1 && (
                      <span className="muted"> 외 {t.담당자들.length - 1}</span>
                    )}
                  </td>
                  <td className="c">{기준설명(t)}</td>
                  <td className="c">
                    {t.마감오프셋 ? `소요 ${t.마감오프셋}일` : '당일'}
                  </td>
                  <td className="c">
                    {{ 그대로: '그대로', 이전영업일: '앞 영업일', 다음영업일: '다음 영업일' }[t.휴일정책] || '그대로'}
                  </td>
                  {/* 기준일과 실제 마감일은 다를 수 있다 — 휴일에 걸리면 옮겨진다.
                      기준일만 보여 주면 "주말인데 왜 생기지?" 로 읽힌다. */}
                  <td className="c">
                    {t.다음생성일 || '—'}
                    {t.다음마감일 && t.다음마감일 !== t.다음생성일 && (
                      <div className="muted" style={{ fontSize: 10 }}>마감 {t.다음마감일}</div>
                    )}
                  </td>
                  <td className="c">{t.사용여부 ? '사용' : '중지'}</td>
                  <td className="c" style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="link-cell"
                      onClick={() => 고치기(t)}>수정</button>
                    <button type="button" className="link-cell" onClick={() => remove(t.id)}>삭제</button>
                  </td>
                </tr>
              ))}
              {!list.length && <tr><td colSpan={10} className="c muted">등록된 정기업무가 없습니다</td></tr>}
            </tbody>
          </table>
          <div className="hint" style={{ marginTop: 8 }}>
            템플릿을 지워도 이미 생성된 업무 건은 남습니다 — 지나간 이력을 지울 이유가 없습니다.
          </div>
        </div>
      </div>
    </div>
  )
}

// 한 주 취합 — 일별 일지를 매일 열어 머릿속에서 더하지 않게 한다.
function WeekView({ meta, who, day }) {
  const [w, setW] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    setErr('')
    portal.get(`/api/work/journal-week?day=${day}&assignee=${who}`)
      .then(setW).catch((e) => setErr(e.message))
  }, [who, day])
  if (err) return <div className="error-box">{err}</div>
  if (!w) return <div className="hint">불러오는 중…</div>
  const S = w.요약
  const 이름 = meta.담당자.find((u) => u.id === who)?.name || ''
  return (
    <>
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
          <b>{w.시작일} ~ {w.종료일} · {이름}</b>
          <span className="muted" style={{ fontSize: 12 }}>일지 제출 {S.일지제출}/7일</span>
        </div>
        <div className="row" style={{ gap: 18, flexWrap: 'wrap' }}>
          <span>완료 <b>{S.완료}</b></span>
          <span>진행중 <b>{S.진행중}</b></span>
          <span>대기 <b>{S.대기}</b></span>
          <span>시작전 <b>{S.시작전}</b></span>
          <span className={S.지연 ? 'w-late' : ''}>지연 <b>{S.지연}</b></span>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <table className="w-exc">
          <thead>
            <tr><th className="c" style={{ width: 96 }}>날짜</th><th className="l">그날 끝낸 일</th>
              <th className="l">그날 마감인데 안 끝난 일</th><th className="l">특이사항 · 확인요청</th></tr>
          </thead>
          <tbody>
            {w.일별.map((d) => (
              <tr key={d.일자}>
                <td className="c">
                  {short(d.일자)}({d.요일})
                  {d.제출 && <span className="muted" style={{ fontSize: 11 }}> ·일지</span>}
                </td>
                <td className="l">{d.완료.map((i) => i.업무명).join(', ') || <span className="muted">—</span>}</td>
                <td className="l">
                  {d.마감.length
                    ? <span className="w-late">{d.마감.map((i) => i.업무명).join(', ')}</span>
                    : <span className="muted">—</span>}
                </td>
                <td className="l">
                  {d.확인요청 && <div className="w-flag">{d.확인요청}</div>}
                  <span className="muted">{d.특이사항}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
          <b>주가 끝나도 남는 업무 · {w.남은업무.length}건</b>
          <span className="muted" style={{ fontSize: 12 }}>다음 주로 넘어가는 짐입니다</span>
        </div>
        <table className="w-exc">
          <thead>
            <tr><th className="c" style={{ width: 52 }}>상태</th><th className="l">업무</th>
              <th className="c" style={{ width: 88 }}>사업부</th><th className="c" style={{ width: 58 }}>갈래</th>
              <th className="c" style={{ width: 92 }}>마감일</th></tr>
          </thead>
          <tbody>
            {w.남은업무.map((i) => (
              <tr key={i.id}>
                <td className="c"><Badge kind={STATUS_CLASS[i.상태]} text={i.상태} /></td>
                <td className="l">{i.업무명}</td>
                <td className="c">{사업부이름(meta, i.사업부)}</td>
                <td className="c">{i.카테고리 || '—'}</td>
                <td className="c">
                  {i.마감일}
                  {i.지연일수 > 0 && <span className="w-late"> +{i.지연일수}</span>}
                </td>
              </tr>
            ))}
            {!w.남은업무.length && (
              <tr><td colSpan={5} className="c muted">남은 업무가 없습니다</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// 상태색은 담당자 색(정체)과 따로 쓴다 — 섞으면 빨강이 "지연"인지 "누구"인지 알 수 없다.
// 검증된 상태 팔레트를 그대로 쓴다. 대기 노랑은 흰 바탕에서 옅어 대비 3:1 을 못 넘는데,
// 더 어둡게 고치는 대신 이름표와 숫자를 늘 함께 두는 것이 이 팔레트의 약속이다.
const 상태색 = {
  완료: '#0ca30c', 진행중: '#2a78d6', 진행: '#2a78d6',
  대기: '#fab219', 지연: '#d03b3b', 시작전: '#94a3b8',
  // 검토대기는 대기와, 취소는 시작전과 같은 색을 쓴다. 여섯 상태에 여섯 색을 주면
  // 색만으로는 어차피 못 가린다. 글자가 이름을 말하고 색은 거들기만 한다.
  검토대기: '#fab219', 취소: '#94a3b8',
}
const 지연색 = 상태색.지연
const 완료색 = 상태색.완료

// 진행률 막대. 눈금 없는 숫자만으로는 누가 밀렸는지 훑어지지 않는다.
function Bar({ pct, color }) {
  return (
    <span style={{ display: 'inline-block', width: 90, height: 8, borderRadius: 4,
                   background: '#eceff3', verticalAlign: 'middle' }}>
      <span style={{ display: 'block', width: `${Math.max(0, Math.min(100, pct))}%`,
                     height: '100%', borderRadius: 4, background: color || '#2a78d6' }} />
    </span>
  )
}

function Tile({ label, value, tone }) {
  return (
    <div className="panel" style={{ flex: '1 1 110px', minWidth: 110, padding: '7px 11px' }}>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 700, color: tone || undefined }}>{value}</div>
    </div>
  )
}

// 팀장 현황판 — "지금 팀이 어디쯤 와 있고, 어디가 밀렸나"에 답한다.
function DashboardTab({ meta, month, onOpen }) {
  const [start, setStart] = useState(monthStart(month))
  const [end, setEnd] = useState(monthEnd(month))
  const [d, setD] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => { setStart(monthStart(month)); setEnd(monthEnd(month)) }, [month])
  useEffect(() => {
    setErr('')
    portal.get(`/api/work/dashboard?start=${start}&end=${end}`)
      .then(setD).catch((e) => setErr(e.message))
  }, [start, end])

  if (err) return <div className="error-box">{err}</div>
  if (!d) return <div className="muted">불러오는 중…</div>
  const T = d.총계
  const 비율 = (n) => (T.전체 ? Math.round((n / T.전체) * 100) : 0)

  const 분포표 = (title, rows, 이름바꾸기) => (
    <div className="panel" style={{ flex: '1 1 320px' }}>
      <b>{title}</b>
      <table className="w-exc" style={{ marginTop: 6 }}>
        <thead>
          <tr><th className="l">구분</th><th className="c" style={{ width: 52 }}>전체</th>
            <th className="c" style={{ width: 52 }}>완료</th><th className="c" style={{ width: 52 }}>지연</th>
            <th className="c" style={{ width: 120 }}>완료율</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.이름}>
              <td className="l">{이름바꾸기 ? 이름바꾸기(r.이름) : r.이름}</td>
              <td className="c">{r.전체}</td>
              <td className="c">{r.완료}</td>
              <td className="c">{r.지연 ? <span className="w-late">{r.지연}</span> : 0}</td>
              <td className="c">
                <Bar pct={r.전체 ? (r.완료 / r.전체) * 100 : 0} color={완료색} />
                <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                  {r.전체 ? Math.round((r.완료 / r.전체) * 100) : 0}%
                </span>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="c muted">해당 기간 업무가 없습니다</td></tr>}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <div className="row" style={{ marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <DateRange start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e) }} />
        <span className="muted" style={{ fontSize: 12 }}>
          기간에 걸치는 업무를 셉니다 · 건수는 성과점수가 아닙니다
        </span>
      </div>

      {/* 지금 무엇을 해야 하는지는 «업무» 탭이 답한다. 여기는 지나간 기간을 돌아보는
          자리라, 끝난 일과 제때 끝냈는지만 본다. 두 화면이 같은 걸 보여 주면 어느
          쪽을 믿어야 할지 모르게 된다. */}
      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Tile label="기간 업무" value={T.전체} />
        <Tile label="완료" value={`${T.완료} (${T.완료율}%)`} tone={완료색} />
        <Tile label="기한 내 완료"
          value={T.기한준수율 == null ? '—' : `${T.기한준수율}%`}
          tone={T.기한준수율 != null && T.기한준수율 < 70 ? 지연색 : 완료색} />
        <Tile label="기한 넘겨 완료" value={T.기한넘겨완료}
          tone={T.기한넘겨완료 ? 지연색 : undefined} />
        <Tile label="아직 안 끝남" value={T.전체 - T.완료 - T.취소} />
        <Tile label="취소" value={T.취소} />
        <Tile label="평균 진행률" value={`${T.평균진행률}%`} />
      </div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>
        지금 급한 것(지연·오늘 마감·검토 대기)은 «업무» 탭에서 봅니다 — 여기는 지나간
        기간을 돌아보는 화면입니다.
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <b>담당자별 — 이 기간에 어땠나</b>
        <table className="w-exc" style={{ marginTop: 6 }}>
          <thead>
            <tr><th className="l">담당자</th><th className="c" style={{ width: 52 }}>전체</th>
              <th className="c" style={{ width: 52 }}>완료</th><th className="c" style={{ width: 78 }}>기한 내</th>
              <th className="c" style={{ width: 62 }}>아직</th>
              <th className="c" style={{ width: 160 }}>평균 진행률</th></tr>
          </thead>
          <tbody>
            {d.담당자.map((p) => (
              <tr key={p.담당자_id}>
                <td className="l">
                  <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2,
                                 background: 색고르기(p.담당자_id), marginRight: 6 }} />
                  {p.담당자명}
                </td>
                <td className="c">{p.전체}</td>
                <td className="c">{p.완료}</td>
                <td className="c">
                  {p.기한준수율 == null ? <span className="muted">—</span>
                    : <span style={{ color: p.기한준수율 < 70 ? 지연색 : undefined,
                                     fontWeight: p.기한준수율 < 70 ? 700 : 400 }}>
                        {p.기한준수율}%
                      </span>}
                </td>
                <td className="c">{p.미완료}</td>
                <td className="c">
                  <Bar pct={p.평균진행률} color={색고르기(p.담당자_id)} />
                  <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>{p.평균진행률}%</span>
                </td>
              </tr>
            ))}
            {!d.담당자.length && (
              <tr><td colSpan={6} className="c muted">해당 기간 업무가 없습니다</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="row" style={{ gap: 12, marginBottom: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {분포표('갈래별', d.갈래)}
        {분포표('사업부별', d.사업부, (c) => 사업부이름(meta, c))}
      </div>

    </>
  )
}

// ── 업무 메인 — 요약 → 팀원 현황 → 통합 목록 ─────────────────────────
// 화면마다 따로 등록하지 않는다. 여기 한 목록이 대시보드·캘린더·팀원별의 같은 원천이다.

const 요약설명 = {
  지연: '마감이 지났는데 안 끝난 것',
  오늘마감: '오늘까지',
  이번주마감: '이번 주 안에',
  // «검토대기»는 팀 어디선가 검토를 기다리는 것, «내 검토»는 내가 눌러야 풀리는 것이다.
  // 한 숫자로 합치면 남이 볼 것까지 내 일로 읽힌다.
  검토대기: '검토를 기다리는 것',
  내검토: '내가 확인자 — 내가 닫아야 끝납니다',
  담당자없음: '아직 맡은 사람이 없는 것',
  일정미지정: '마감을 아직 안 정한 것',
}
const 요약색 = {
  지연: 상태색.지연, 오늘마감: '#334155', 이번주마감: '#334155',
  검토대기: 상태색.대기, 내검토: 상태색.대기, 담당자없음: '#4a3aa7',
  일정미지정: 상태색.시작전,
}

function 빈줄({ children }) {
  return <div className="muted" style={{ padding: '14px 4px', textAlign: 'center' }}>{children}</div>
}

// 마감일을 읽기 쉽게. 오늘·내일은 날짜보다 말이 빨리 읽힌다.
function 마감표시({ item, 오늘 }) {
  if (!item.마감일) return <span className="muted">일정 미지정</span>
  const n = item.남은일수
  const 글 = n === 0 ? '오늘' : n === 1 ? '내일' : n === -1 ? '어제' : short(item.마감일)
  if (item.지연여부) {
    return <span className="w-late" style={{ fontWeight: 700 }}>{short(item.마감일)} · {item.지연일수}일 지남</span>
  }
  return <span style={{ fontWeight: n <= 1 ? 700 : 400 }}>{글}</span>
}

// 알림을 언제 받을지. 기본값이 맞지 않는 사람이 늘 있고, 못 바꾸면 알림을 꺼 버린다.
function 알림설정모달({ meta, onClose }) {
  const [p, setP] = useState(null)
  const [err, setErr] = useState('')
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    portal.get('/api/work/notify-prefs').then(setP).catch((e) => setErr(e.message))
  }, [])
  const set = (k, v) => { setP((x) => ({ ...x, [k]: v })); setSaved(false) }
  const save = async () => {
    setErr('')
    try { await portal.post('/api/work/notify-prefs', p); setSaved(true) }
    catch (e) { setErr(`저장하지 못했습니다 — ${e.message}`) }
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 460 }}>
        <div className="modal-header">
          <h3>내 알림 설정</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body tight">
          {!p ? <div className="muted">불러오는 중…</div> : (
            <>
              <div className="hint" style={{ marginBottom: 10 }}>
                지금은 앱 안 종(🔔)으로만 갑니다. 같은 알림은 하루에 한 번만 옵니다.
              </div>
              <label className="row" style={{ gap: 6, cursor: 'pointer', marginBottom: 8 }}>
                <input type="checkbox" checked={!!p.마감전}
                  onChange={(e) => set('마감전', e.target.checked)} />
                <span>마감</span>
                <input type="number" min={0} max={14} style={{ width: 56 }}
                  value={p.마감전일수} disabled={!p.마감전}
                  onChange={(e) => set('마감전일수', Number(e.target.value))} />
                <span>일 전에 알림</span>
              </label>
              <label className="row" style={{ gap: 6, cursor: 'pointer', marginBottom: 8 }}>
                <input type="checkbox" checked={!!p.당일}
                  onChange={(e) => set('당일', e.target.checked)} />
                <span>마감 당일 알림</span>
              </label>
              <label className="row" style={{ gap: 6, cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={!!p.지연}
                  onChange={(e) => set('지연', e.target.checked)} />
                <span>마감이 지나면 날마다 알림</span>
              </label>
              {err && <div className="error-box">{err}</div>}
              <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                {saved && <span className="muted">저장했습니다</span>}
                <button type="button" className="btn" onClick={save}>저장</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// 공휴일 — 나라가 정한 날은 서버가 계산해 준다(설날·추석은 음력이라 사람이 넣으면
// 반드시 빠뜨린다). 화면은 우리 사정으로 더 쉬는 날을 얹고, 쉬지 않는 날을 덜어낸다.
function 공휴일모달({ onClose, onSaved }) {
  const [list, setList] = useState(null)
  const [일자, set일자] = useState('')
  const [이름, set이름] = useState('')
  const [err, setErr] = useState('')
  const load = () => portal.get('/api/work/holidays').then(setList).catch((e) => setErr(e.message))
  useEffect(load, [])
  const add = async () => {
    setErr('')
    try { await portal.post('/api/work/holidays', { 일자, 이름 }); set일자(''); set이름(''); load(); onSaved?.() }
    catch (e) { setErr(e.message) }
  }
  const del = async (d) => {
    try { await portal.del(`/api/work/holidays/${d}`); load(); onSaved?.() }
    catch (e) { setErr(e.message) }
  }
  // 나라 달력의 날은 지워 봐야 다음에 다시 계산돼 돌아온다. '제외'로 덮어 둔다.
  const 토글 = async (h) => {
    setErr('')
    try {
      if (h.쉼) await portal.post('/api/work/holidays', { 일자: h.일자, 이름: h.이름, 구분: '제외' })
      else await portal.del(`/api/work/holidays/${h.일자}`)
      load(); onSaved?.()
    } catch (e) { setErr(e.message) }
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 460 }}>
        <div className="modal-header">
          <h3>공휴일</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body tight">
          <div className="hint" style={{ marginBottom: 10 }}>
            정기업무 마감이 이 날에 걸리면 «휴일 처리» 설정대로 앞뒤 영업일로 옮깁니다.
            토·일요일과 나라가 정한 공휴일(대체공휴일 포함)은 <b>넣지 않아도 자동으로</b>
            잡힙니다. 아래는 우리 사정으로 더 쉬는 날을 얹거나, 쉬지 않는 날을 빼는 자리입니다.
          </div>
          <div className="row" style={{ gap: 6, marginBottom: 10 }}>
            <input type="date" value={일자} onChange={(e) => set일자(e.target.value)} />
            <input placeholder="이름 (예: 창립기념일)" value={이름} style={{ flex: 1 }}
              onChange={(e) => set이름(e.target.value)} />
            <button type="button" className="btn" disabled={!일자} onClick={add}>추가</button>
          </div>
          {err && <div className="error-box">{err}</div>}
          {!list ? <div className="muted">불러오는 중…</div>
            : !list.length ? <div className="muted">등록된 공휴일이 없습니다.</div>
              : (
                <table className="w-exc">
                  <tbody>
                    {list.map((h) => (
                      <tr key={h.일자} style={{ opacity: h.쉼 ? 1 : 0.5 }}>
                        <td className="l">{h.일자}</td>
                        <td className="l">
                          {h.이름 || '—'}
                          {h.자동 && <span className="muted" style={{ fontSize: 10 }}> · 나라 달력</span>}
                          {!h.쉼 && <span className="w-flag">이날은 일함</span>}
                        </td>
                        <td className="c" style={{ whiteSpace: 'nowrap' }}>
                          {h.자동
                            ? <button type="button" className="link-cell" onClick={() => 토글(h)}>
                                {h.쉼 ? '쉬지 않음으로' : '다시 쉬는 날로'}
                              </button>
                            : <button type="button" className="link-cell"
                                onClick={() => del(h.일자)}>지우기</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  )
}

// 취소는 사유를 받는다. 왜 안 하기로 했는지가 없으면 나중에 같은 일을 다시 꺼낸다.
function 취소모달({ item, onClose, onDone }) {
  const [사유, set사유] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const go = async () => {
    setBusy(true); setErr('')
    try { await portal.post(`/api/work/items/${item.id}/status`, { 상태: '취소', 사유 }); onDone(); onClose() }
    catch (e) { setErr(e.message); setBusy(false) }
  }
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 440 }}>
        <div className="modal-header">
          <h3>업무 취소</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body tight">
          <div className="muted" style={{ marginBottom: 10, fontSize: 12 }}>{item.업무명}</div>
          <div className="hint" style={{ marginBottom: 10 }}>
            취소한 업무는 목록에서 접히지만 지워지지는 않습니다 — 왜 안 하기로 했는지가 남습니다.
          </div>
          <input value={사유} onChange={(e) => set사유(e.target.value)} style={{ width: '100%' }}
            placeholder="예: 중복 등록 / 상위 일정 취소 / 다른 팀으로 이관" />
          {err && <div className="error-box" style={{ marginTop: 8 }}>{err}</div>}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn secondary" onClick={onClose}>그만두기</button>
          <button type="button" className="btn" disabled={busy || !사유.trim()} onClick={go}>
            {busy ? '처리 중…' : '취소로 바꾸기'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MainTab({ meta, reload, bump, onOpen, openId }) {
  const [scope, setScope] = useState(meta.팀장 ? 'team' : 'mine')
  const [sum, setSum] = useState(null)
  const [team, setTeam] = useState(null)
  const [rows, setRows] = useState(null)
  const [err, setErr] = useState('')
  const [saveErr, setSaveErr] = useState('')

  // 필터 — 요약 카드 · 팀원 클릭 · 손으로 고른 것이 모두 여기 모인다
  const [flag, setFlag] = useState(null)
  const [who, setWho] = useState(null)
  const [status, setStatus] = useState([])
  const [reg, setReg] = useState('')
  const [rep, setRep] = useState('')
  const [pri, setPri] = useState('')
  // 사업자가 여섯이라 한 화면에 섞어 놓으면 어느 회사 일인지 눈으로 골라야 한다.
  // 탭으로 갈라 한 번에 한 회사만 본다. «전체»는 그대로 둔다 — 하루를 시작할 때는
  // 회사를 가리지 않고 훑는 쪽이 맞다.
  const [사업부, set사업부] = useState('전체')
  const [q, setQ] = useState('')
  // 치는 동안에는 묻지 않는다. 한 글자마다 묻던 것을 잠깐 멈춘 뒤 한 번만 묻는다 —
  // «법인카드» 를 치면 네 번 나가던 요청이 한 번이 된다.
  const [검색어, set검색어] = useState('')
  useEffect(() => {
    const t = setTimeout(() => set검색어(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])
  // 응답이 뒤섞이는 것을 막는 번호. 늦게 온 옛 응답은 버린다.
  const 요청번호 = useRef(0)
  const [view, setView] = useState('list')
  // 사업부가 1순위다. 회사를 오가며 일하면 같은 회사 것을 몰아서 처리하게 되고,
  // 그때 목록이 회사별로 서 있지 않으면 매번 눈으로 골라내야 한다.
  const [갈래, set갈래] = useState('')
  const [정렬, set정렬] = useState('segment')
  const [완료보기, set완료보기] = useState(false)
  const [edit, setEdit] = useState(null)
  const [알림설정, set알림설정] = useState(false)
  const [공휴일, set공휴일] = useState(false)
  const [정기, set정기] = useState(false)
  const [대기, set대기] = useState(null)      // 대기 사유를 받을 업무
  const [취소, set취소] = useState(null)      // 취소 사유를 받을 업무
  // 방금 완료·취소로 바꾼 줄. 규칙대로 곧장 접으면 고친 것이 눈앞에서 사라져
  // 눌린 건지 아닌지를 알 수 없다. 이번 화면에서는 회색으로 남겨 두고, 새로고침하면
  // 그때 접힌다 — 화면을 다시 열었다는 건 그 일은 끝났다고 본 것이다.
  const [방금닫음, set방금닫음] = useState(() => new Set())
  const 닫은것기억 = (id) => set방금닫음((s) => new Set(s).add(id))

  const 오늘 = meta.오늘

  // 달력의 «예정»도 목록과 같은 조건으로 받는다 — 조건이 두 곳에서 갈리면
  // «걸러 놨는데 달력에만 남의 것이 뜬다»가 된다. 정렬은 예정에 뜻이 없어 뺀다.
  const 예정질의 = useMemo(() => {
    const p = new URLSearchParams()
    // 카드와 같은 범위로 본다 — 안 실으면 «내 업무»인데 목록·달력엔 팀 전체가 선다
    p.set('scope', scope)
    if (사업부 !== '전체') p.set('segments', 사업부)
    if (flag) p.set('flags', flag)
    if (who) p.set('assignee', who)
    if (status.length) p.set('statuses', status.join(','))
    if (reg) p.set('regs', reg)
    if (rep) p.set('repeats', rep)
    if (pri) p.set('priority', pri)
    if (갈래) p.set('categories', 갈래)
    if (검색어) p.set('q', 검색어)
    return p.toString()
  }, [scope, 사업부, flag, who, status, reg, rep, pri, 갈래, 검색어])

  const load = useCallback(() => {
    setErr('')
    // 글자를 칠 때마다 요청이 나가는데, 늦게 온 옛 응답이 새 응답을 덮으면
    // «칸에는 법인카드라고 적혀 있는데 목록은 비어 있는» 화면이 된다(실제로 그랬다).
    // 한글은 조합 중간값(«법인카ㄷ»)이 0건이라 더 잘 겹친다. 번호를 달아 마지막 것만 쓴다.
    const 내번호 = ++요청번호.current
    const 최신 = () => 내번호 === 요청번호.current
    portal.get(`/api/work/summary?scope=${scope}`)
      .then((r) => 최신() && setSum(r)).catch((e) => 최신() && setErr(e.message))
    if (meta.팀장 && scope === 'team') {
      portal.get('/api/work/team').then((r) => 최신() && setTeam(r)).catch(() => setTeam(null))
    } else setTeam(null)
    const p = new URLSearchParams()
    // 카드와 같은 범위로 본다 — 안 실으면 «내 업무»인데 목록·달력엔 팀 전체가 선다
    p.set('scope', scope)
    if (사업부 !== '전체') p.set('segments', 사업부)
    if (flag) p.set('flags', flag)
    if (who) p.set('assignee', who)
    if (status.length) p.set('statuses', status.join(','))
    if (reg) p.set('regs', reg)
    if (rep) p.set('repeats', rep)
    if (pri) p.set('priority', pri)
    if (갈래) p.set('categories', 갈래)
    if (검색어) p.set('q', 검색어)
    p.set('order', 정렬)
    portal.get(`/api/work/items?${p}`)
      .then((r) => 최신() && setRows(r)).catch((e) => 최신() && setErr(e.message))
  }, [scope, 사업부, flag, who, status, reg, rep, pri, 갈래, 검색어, 정렬, meta.팀장])
  useEffect(() => { load() }, [load, reload])

  const 필터있음 = flag || who || status.length || reg || rep || pri || 갈래 || q.trim()
  const 초기화 = () => {
    setFlag(null); setWho(null); setStatus([]); setReg(''); setRep(''); setPri(''); set갈래(''); setQ('')
  }
  const 이름 = (id) => meta.담당자.find((u) => u.id === id)?.name || ''

  // 목록에서 바로 고친다 — 한 건 고치자고 상세를 열었다 닫는 건 하루에 수십 번이면 일이다
  const 빠른저장 = async (id, body) => {
    setSaveErr('')
    try { await portal.post(`/api/work/items/${id}`, body); load(); bump() }
    catch (e) { setSaveErr(`저장하지 못했습니다 — ${e.message}`) }
  }
  // 대기와 취소는 사유를 받아야 한다. 단추 한 번으로 넘기면 사유 없는 대기가 쌓이고,
  // 그게 대기 장기화의 시작이다.
  // 시작전인데 진행률을 올리면 서버가 진행중으로 올려 준다 — 상태를 따로 누르게 하지 않는다.
  const 빠른진행률 = async (item, 진행률) => {
    setSaveErr('')
    try { await portal.post(`/api/work/items/${item.id}/progress`, { 진행률 }); load(); bump() }
    catch (e) { setSaveErr(`진행률을 바꾸지 못했습니다 — ${e.message}`) }
  }
  // 팀장은 아무나 세울 수 있고(수정), 팀원은 자기 자신만 가져올 수 있다(맡기).
  // 두 길은 서버에서 규칙이 다르므로 여기서 갈라 부른다 — 화면에는 고르개 하나로 보인다.
  const 담당지정 = async (item, uid) => {
    // 팀원이 빈 칸(«지정…»)을 다시 고른 것은 «아무것도 안 하겠다»는 뜻이다.
    // 그대로 맡기를 부르면 손가락이 미끄러진 사람에게 업무가 붙는다.
    if (!uid && !meta.팀장) return
    setSaveErr('')
    try {
      if (meta.팀장) {
        await portal.post(`/api/work/items/${item.id}`,
          { 담당자들: uid ? [{ id: uid }] : [] })
      } else {
        await portal.post(`/api/work/items/${item.id}/claim`)
      }
      load(); bump()
    } catch (e) { setSaveErr(`담당자를 정하지 못했습니다 — ${e.message}`) }
  }

  const 빠른상태 = async (item, 상태) => {
    if (상태 === '대기') return set대기(item)
    if (상태 === '취소') return set취소(item)
    setSaveErr('')
    try {
      await portal.post(`/api/work/items/${item.id}/status`, { 상태 })
      if (['완료', '취소'].includes(상태)) 닫은것기억(item.id)
      load(); bump()
    }
    catch (e) { setSaveErr(`상태를 바꾸지 못했습니다 — ${e.message}`) }
  }

  // 끝난 일은 기본으로 접는다. 다만 «완료»나 «취소»를 직접 골랐으면 접으면 안 된다 —
  // 서버는 보내 주는데 화면이 도로 걸러내서, 완료 필터를 걸면 "조건에 맞는 업무가
  // 없습니다"가 떴다. 골라서 보겠다는 사람에게 숨기는 것은 접는 게 아니라 고장이다.
  const 닫힘고름 = status.some((s) => ['완료', '취소'].includes(s))
  const 보일것 = useMemo(() => {
    if (!rows) return null
    if (완료보기 || 닫힘고름) return rows
    return rows.filter((i) => !['완료', '취소'].includes(i.상태) || 방금닫음.has(i.id))
  }, [rows, 완료보기, 닫힘고름, 방금닫음])
  const 닫힌수 = (rows || []).filter((i) => ['완료', '취소'].includes(i.상태)).length

  return (
    <>
      {/* ── 사업부 탭 ── */}
      <div className="row" style={{ gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
        {[['전체', '전체'], ...(meta.사업부 || []).map((sg) => [sg.code, sg.name])]
          .map(([code, name]) => (
            <button key={code} type="button"
              className={'data-tab' + (사업부 === code ? ' active' : '')}
              onClick={() => set사업부(code)}>
              {code !== '전체' && (
                <span style={{ display: 'inline-block', width: 3, height: 10, borderRadius: 2,
                               background: 사업부색[code] || '#cbd5e1', marginRight: 5,
                               verticalAlign: 'middle' }} />
              )}
              {name}
            </button>
          ))}
      </div>

      {/* ── 범위 전환 ── */}
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <고르기 값={scope} 목록={[['mine', '내 업무'], ['team', '팀 전체']]}
          막힘={(k) => k === 'team' && !meta.팀장}
          onChange={(k) => { setScope(k); setWho(null) }} />
        <span className="row" style={{ gap: 8 }}>
          <span className="muted" style={{ fontSize: 11 }}>기준 시각 한국(KST) · 오늘 {오늘}</span>
          <button type="button" className="link-cell" onClick={() => set알림설정(true)}>알림 설정</button>
          {meta.팀장 && (
            <button type="button" className="link-cell" onClick={() => set공휴일(true)}>공휴일</button>
          )}
        </span>
      </div>

      {err && <div className="error-box">{err}</div>}
      {saveErr && (
        <div className="error-box" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{saveErr}</span>
          <button type="button" className="link-cell" onClick={() => { setSaveErr(''); load() }}>다시 불러오기</button>
        </div>
      )}

      {/* ── 요약 카드 ── */}
      {!sum ? <빈줄>불러오는 중…</빈줄> : (
        <>
          <div className="row" style={{ gap: 7, marginBottom: 4, flexWrap: 'wrap' }}>
            {['지연', '오늘마감', '이번주마감', '담당자없음', '내검토', '검토대기', '일정미지정'].map((k) => {
              const n = sum.카드[k] || 0
              const on = flag === k
              return (
                <button key={k} type="button" className="panel"
                  onClick={() => { setFlag(on ? null : k); }}
                  style={{
                    flex: '0 1 158px', minWidth: 120, maxWidth: 190, textAlign: 'left',
                    padding: '7px 11px', marginBottom: 0, cursor: 'pointer',
                    font: 'inherit', borderLeft: `3px solid ${n ? 요약색[k] : '#e5e9ef'}`,
                    outline: on ? `2px solid ${요약색[k]}` : 'none',
                  }}>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {{ 일정미지정: '일정 미지정', 내검토: '내 검토', 담당자없음: '담당자 없음' }[k] || k}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15,
                                color: n ? 요약색[k] : '#b8c0cc' }}>{n}</div>
                  <div className="muted" style={{ fontSize: 10 }}>{요약설명[k]}</div>
                </button>
              )
            })}
          </div>
          <div className="muted" style={{ fontSize: 10, marginBottom: 10 }}>
            {sum.겹침.join(' · ')} — 카드 합은 전체 건수와 다릅니다
          </div>
        </>
      )}

      {/* ── 팀원별 현황 ── */}
      {meta.팀장 && scope === 'team' && (
        <div className="panel" style={{ marginBottom: 10 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <b>팀원별 현황</b>
          </div>
          {!team ? <빈줄>불러오는 중…</빈줄> : (
            <table className="w-exc">
              <thead>
                <tr>
                  <th className="l" style={{ width: 88 }}>담당자</th>
                  <th className="c" style={{ width: 44 }}>미완료</th><th className="c" style={{ width: 38 }}>지연</th>
                  <th className="c" style={{ width: 44 }}>오늘</th><th className="c" style={{ width: 38 }}>검토</th>
                  {/* 팀장이 월요일에 가장 먼저 묻는 것은 «이번 주에 누가 무엇을 하나» 다.
                      숫자만으로는 그 답이 안 나와서, 이번 주 마감인 일을 사람별로 편다. */}
                  <th className="l" style={{ width: 290 }}>이번 주 할 일</th>
                  <th className="l">가장 가까운 마감</th>
                  <th className="l" style={{ width: 210 }}>도움·결정 필요</th>
                </tr>
              </thead>
              <tbody>
                {team.팀원.map((p) => (
                  <tr key={p.담당자_id} className={who === p.담당자_id ? 'on' : ''}>
                    <td className="l">
                      <button type="button" className="link-cell"
                        onClick={() => setWho(who === p.담당자_id ? null : p.담당자_id)}>
                        {p.담당자명}
                      </button>
                    </td>
                    <td className="c">{p.미완료}</td>
                    <td className="c">{p.지연 ? <span className="w-late">{p.지연}</span> : 0}</td>
                    <td className="c">{p.오늘마감}</td>
                    <td className="c">{p.검토대기 ? <span style={{ color: 상태색.대기, fontWeight: 700 }}>{p.검토대기}</span> : 0}</td>
                    <td className="l">
                      {(() => {
                        const w = p.이번주 || { 목록: [], 전체: 0, 완료: 0 }
                        if (!w.전체) return <span className="muted">— 이번 주 마감 없음</span>
                        return (
                          <>
                            <div className="row" style={{ gap: 6, marginBottom: 3 }}>
                              <span style={{ display: 'inline-block', width: 70, height: 6,
                                             borderRadius: 3, background: '#eceff3', flex: 'none' }}>
                                <span style={{ display: 'block', height: '100%', borderRadius: 3,
                                               width: `${Math.round((w.완료 / w.전체) * 100)}%`,
                                               background: 상태색.완료 }} />
                              </span>
                              <span className="muted" style={{ fontSize: 11 }}>
                                {w.완료} / {w.전체}건 완료
                              </span>
                            </div>
                            {w.목록.slice(0, 4).map((i) => (
                              <div key={i.id} style={{ lineHeight: 1.6 }}>
                                <span style={{ display: 'inline-block', width: 3, height: 10,
                                               borderRadius: 2, marginRight: 5,
                                               verticalAlign: 'middle',
                                               background: 사업부색[i.사업부] || '#cbd5e1' }} />
                                <button type="button" className="link-cell"
                                  onClick={() => onOpen(i.id)}>{i.업무명}</button>
                                <span style={{ marginLeft: 6 }}>
                                  <마감표시 item={i} 오늘={오늘} />
                                </span>
                              </div>
                            ))}
                            {w.목록.length > 4 && (
                              <div className="muted" style={{ fontSize: 10 }}>
                                외 {w.목록.length - 4}건
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </td>
                    <td className="l">
                      {p.가까운마감.length === 0
                        ? <span className="muted">— 맡은 업무 없음</span>
                        : p.가까운마감.map((i) => (
                          <div key={i.id} style={{ lineHeight: 1.6 }}>
                            <button type="button" className="link-cell" onClick={() => onOpen(i.id)}>
                              {i.업무명}
                            </button>
                            <span style={{ marginLeft: 6 }}><마감표시 item={i} 오늘={오늘} /></span>
                          </div>
                        ))}
                    </td>
                    <td className="l">
                      {p.도움필요.length === 0 ? <span className="muted">—</span>
                        : p.도움필요.map((i) => (
                          <div key={i.id} style={{ lineHeight: 1.6 }}>
                            <Badge kind={STATUS_CLASS[i.상태]} text={i.상태} />
                            <button type="button" className="link-cell" onClick={() => onOpen(i.id)}>
                              {i.업무명}
                            </button>
                            {i.대기사유 && <span className="muted" style={{ fontSize: 10 }}> · {i.대기사유}</span>}
                          </div>
                        ))}
                    </td>
                  </tr>
                ))}
                {!team.팀원.length && <tr><td colSpan={8}><빈줄>업무관리를 쓰는 사람이 없습니다</빈줄></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── 지금 걸린 필터 ── */}
      {필터있음 && (
        <div className="row" style={{ gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 11 }}>지금 보는 조건</span>
          {flag && <span className="w-flag">{flag}</span>}
          {who && <span className="w-flag">담당 {이름(who)}</span>}
          {status.map((s) => <span key={s} className="w-flag">{s}</span>)}
          {reg && <span className="w-flag">{reg}</span>}
          {rep && <span className="w-flag">{rep}</span>}
          {pri && <span className="w-flag">우선 {pri}</span>}
          {갈래 && <span className="w-flag">{갈래}</span>}
          {q.trim() && <span className="w-flag">"{q.trim()}"</span>}
          <button type="button" className="btn tiny secondary" onClick={초기화}>모두 해제</button>
        </div>
      )}

      {/* ── 통합 목록 ── */}
      <div className="panel">
        <div className="row" style={{ gap: 7, marginBottom: 8, flexWrap: 'wrap' }}>
          <고르기 값={view} onChange={setView}
            목록={[['list', '목록'], ['week', '주간'], ['month', '월간']]} />
          {meta.팀장 && scope === 'team' && (
            <select value={who || ''} onChange={(e) => setWho(Number(e.target.value) || null)}>
              <option value="">담당자 전체</option>
              {meta.담당자.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <select value={reg} onChange={(e) => setReg(e.target.value)}>
            <option value="">등록 전체</option>
            {(meta.등록구분 || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={rep} onChange={(e) => setRep(e.target.value)}>
            <option value="">반복 전체</option>
            {(meta.반복구분 || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={pri} onChange={(e) => setPri(e.target.value)}>
            <option value="">우선순위 전체</option>
            {meta.우선순위.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={갈래} onChange={(e) => set갈래(e.target.value)}>
            <option value="">갈래 전체</option>
            {(meta.카테고리 || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input placeholder="업무명 검색" value={q} style={{ width: 150 }}
            onChange={(e) => setQ(e.target.value)} />
          {/* 기본은 급한 순이다. 한 회사 것을 몰아서 처리할 때는 사업부로 묶어 본다. */}
          <select value={정렬} onChange={(e) => set정렬(e.target.value)} title="정렬">
            <option value="segment">사업부 순</option>
            <option value="risk">급한 순</option>
            <option value="due">마감 순</option>
          </select>
          <button type="button" className="btn" onClick={() => setEdit({})}>업무 등록</button>
          {/* 팀원도 자기 반복 업무는 스스로 건다. 남에게 거는 것과 «이후 회차»만 팀장 몫이다 —
              부탁해야만 걸 수 있으면 결국 아무도 안 건다. */}
          <button type="button" className="btn secondary" onClick={() => set정기(true)}>
            정기업무 설정
          </button>
        </div>
        <div className="row" style={{ gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
          {meta.상태.map((s) => (
            <button key={s} type="button"
              className={'data-tab' + (status.includes(s) ? ' active' : '')}
              onClick={() => setStatus(status.includes(s) ? status.filter((x) => x !== s) : [...status, s])}>
              {s}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          {/* 흐린 글씨 링크였더니 접혀 있다는 것을 아무도 못 봤다 — 끝낸 일이 사라진 줄 안다.
              상태 칩과 같은 모양으로 두어 '누르는 것'으로 읽히게 한다. */}
          {닫힌수 > 0 && !닫힘고름 && (
            <button type="button" className={'data-tab' + (완료보기 ? ' active' : '')}
              onClick={() => set완료보기(!완료보기)}>
              완료·취소 {닫힌수}건 {완료보기 ? '접기' : '보기'}
            </button>
          )}
        </div>

        {view === 'list' && (
          !보일것 ? <빈줄>불러오는 중…</빈줄>
            : 보일것.length === 0 ? <빈줄>{필터있음 ? '조건에 맞는 업무가 없습니다' : '아직 업무가 없습니다 — 위 «업무 등록» 으로 시작하세요'}</빈줄>
              : <목록표 rows={보일것} meta={meta} 오늘={오늘} onOpen={onOpen}
                  onStatus={빠른상태} onSave={빠른저장} onProgress={빠른진행률} onClaim={담당지정}
                  방금닫음={방금닫음} openId={openId} />
        )}
        {view === 'week' && <주간표 rows={보일것 || []} meta={meta} onOpen={onOpen} 질의={예정질의} />}
        {view === 'month' && <월간표 rows={보일것 || []} meta={meta} onOpen={onOpen} 질의={예정질의} />}
        {view !== 'list' && <사업부범례 meta={meta} />}
      </div>

      {edit && <WorkModal meta={meta} item={edit.id ? edit : null}
        onClose={() => setEdit(null)} onSaved={() => { load(); bump() }} />}
      {정기 && <RecurringModal meta={meta} onClose={() => set정기(false)}
        onSaved={() => { load(); bump() }} />}
      {대기 && <HoldModal item={대기} onClose={() => set대기(null)}
        onSaved={() => { load(); bump() }} />}
      {취소 && <취소모달 item={취소} onClose={() => set취소(null)}
        onDone={() => { 닫은것기억(취소.id); load(); bump() }} />}
      {알림설정 && <알림설정모달 meta={meta} onClose={() => set알림설정(false)} />}
      {공휴일 && <공휴일모달 onClose={() => set공휴일(false)} onSaved={() => load()} />}
    </>
  )
}

// 업무명·담당자·마감일이 먼저 읽히게. 나머지는 옆에 붙인다.
function 목록표({ rows, meta, 오늘, onOpen, onStatus, onSave, onProgress, onClaim, 방금닫음, openId }) {
  return (
    <table>
      <thead>
        <tr>
          {/* 어느 회사 · 얼마나 급한가 · 무슨 갈래인가. 제목 아래 흐린 글씨로 두었더니
              줄을 하나하나 읽어야 보였다. 왼쪽에 칸으로 세워 두면 훑어진다. */}
          <th className="c" style={{ width: 124 }}>사업부</th>
          <th className="c" style={{ width: 62 }}>우선</th>
          <th className="c" style={{ width: 72 }}>갈래</th>
          <th className="l">업무</th>
          <th className="c" style={{ width: 84 }}>담당자</th>
          <th className="c" style={{ width: 128 }}>마감</th>
          <th className="c" style={{ width: 92 }}>상태</th>
          <th className="c" style={{ width: 132 }}>진행률</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((i) => (
          <tr key={i.id} className={(openId === i.id ? 'on' : '')
            + (방금닫음?.has(i.id) ? ' w-just-closed' : '')}>
            {/* 왼쪽에 색 띠. 세로로 훑을 때 같은 회사 일이 뭉쳐 보인다.
                셋 다 목록에서 바로 고친다 — 사업부를 잘못 골라 놓고 고치자고 상세를
                열었다 닫는 건 하루에도 여러 번이면 일이다. */}
            <td className="c" style={{
              borderLeft: `3px solid ${사업부색[i.사업부] || 'transparent'}`,
              paddingLeft: 6,
            }}>
              <select className="w-inline" value={i.사업부 || meta.기본사업부}
                disabled={!고칠수있나(meta, i)}
                onChange={(e) => onSave(i.id, { 사업부: e.target.value })}>
                {meta.사업부.map((sg) => (
                  <option key={sg.code} value={sg.code}>{sg.name}</option>
                ))}
              </select>
            </td>
            {/* 우선순위에 색을 따로 주지 않는다 — 빨강은 이미 «지연»이 쓰고 있어서,
                여기까지 색을 주면 무엇이 급한 건지 되레 안 보인다. 굵기로만 가린다. */}
            <td className="c">
              <select className="w-inline" value={i.우선순위 || '보통'}
                disabled={!고칠수있나(meta, i)}
                style={{ fontWeight: i.우선순위 === '높음' ? 700 : 400 }}
                onChange={(e) => onSave(i.id, { 우선순위: e.target.value })}>
                {meta.우선순위.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </td>
            <td className="c">
              <select className="w-inline" value={i.카테고리 || ''}
                disabled={!고칠수있나(meta, i)}
                onChange={(e) => onSave(i.id, { 카테고리: e.target.value })}>
                <option value="">—</option>
                {meta.카테고리.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </td>
            <td className="l">
              <button type="button" className="link-cell" style={{ fontWeight: 600 }}
                onClick={() => onOpen(i.id)}>{i.업무명}</button>
              {i.반복구분 === '정기' && <span className="w-badge w-t-cycle" style={{ marginLeft: 4 }}>정기</span>}
              {/* 협업은 담당자 칸에 사람이 여럿 적히므로 뱃지가 같은 말을 두 번 한다.
                  누가 맡겼는지를 알아야 하는 건 요청뿐이다. */}
              {i.유형 === '요청' && i.지시자명 && (
                <span className="w-badge w-t-ask" style={{ marginLeft: 3 }}>
                  {i.지시자명} 요청
                </span>
              )}
              {i.검토필요 && <span className="w-flag" style={{ marginLeft: 3 }}>검토 대상</span>}
              {/* 담당이 내가 아닌데 목록에 있는 줄 — 왜 있는지를 줄에 적어 둔다 */}
              {i.내역할 && (
                <span className="w-badge w-t-order" style={{ marginLeft: 3 }}>{i.내역할}</span>
              )}
              {방금닫음?.has(i.id) && (
                <span className="muted" style={{ marginLeft: 4, fontSize: 10 }}>
                  {i.상태} — 새로고침하면 접힙니다
                </span>
              )}
              {i.대기사유 && (
                <div className="muted" style={{ fontSize: 10 }}>대기: {i.대기사유}</div>
              )}
            </td>
            {/* 담당자가 여럿이면 이름을 다 늘어놓는 대신 «외 N». 손을 올리면 다 나온다 —
                이름을 다 적으면 칸이 넘쳐 업무명 자리를 먹는다. */}
            <td className="c" title={(i.담당자들 || []).map((a) => a.name).join(', ')}>
              {/* 주인이 없는 회차는 비워 두면 «빠뜨린 줄»처럼 보인다. 그 자리에서 바로
                  가져갈 수 있게 단추를 둔다 — 팀장이 매달 지정해 줄 때까지 기다리면
                  지정이 늦은 달에는 아무도 손을 안 댄 채 마감이 지나간다. */}
              {/* 고르개 하나로 끝낸다. 팀원에게는 자기 이름만 뜨므로 «내가 맡기»와 같은
                  일이 되고, 팀장은 같은 자리에서 남으로 바꾸거나 미지정으로 되돌린다.
                  처음에는 미지정일 때만 고르개를 띄웠는데, 그러면 이미 주인이 있는 줄은
                  «수정» 창을 열어야만 바뀐다 — 매달 주인이 바뀌는 일이라 그 자리에서
                  바꿀 수 있어야 한다.
                  담당자가 여럿인 업무는 고르개 하나로 뭉개지므로 이름만 적고 «수정»으로
                  보낸다. 끝난 업무(완료·취소)는 주인을 바꿀 일이 없다. */}
              {(i.담당자들 || []).length > 1 ? (
                <span title="여러 명 — «수정»에서 바꿉니다">
                  {i.담당자명}<span className="muted"> 외 {i.담당자들.length - 1}</span>
                </span>
              ) : ['완료', '취소'].includes(i.상태)
                || (!meta.팀장 && (i.담당자들 || []).length) ? (
                <span className={(i.담당자들 || []).length ? '' : 'muted'}
                  style={(i.담당자들 || []).length ? undefined : { fontSize: 11 }}>
                  {i.담당자명 || '미지정'}
                </span>
              ) : (
                <select className="w-inline" value={(i.담당자들 || [])[0]?.user_id || ''}
                  onChange={(e) => onClaim(i, e.target.value ? Number(e.target.value) : null)}>
                  <option value="">
                    {(i.담당자들 || []).length ? '미지정으로' : '지정…'}
                  </option>
                  {고를사람(meta).map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              )}
            </td>
            <td className="c">
              <input type="date" value={i.마감일 || ''} style={{ width: 118 }}
                disabled={!고칠수있나(meta, i)}
                onChange={(e) => onSave(i.id, { 마감일: e.target.value || null })} />
              <div style={{ fontSize: 10 }}><마감표시 item={i} 오늘={오늘} /></div>
            </td>
            {/* 상태는 보여 주기만 하던 칸이었고 바꾸는 단추는 옆 칸에 따로 있었다.
                바꾸려는 사람은 상태가 적힌 자리를 먼저 누른다 — 거기서 바꾸게 한다.
                대기·취소는 사유를 받아야 해서 onStatus 가 모달로 넘긴다. */}
            <td className="c">
              <select className="w-status" value={i.상태}
                disabled={!고칠수있나(meta, i) && !검토차례(meta, i)}
                style={{ color: 상태색[i.상태], fontWeight: 600 }}
                onChange={(e) => onStatus(i, e.target.value)}>
                {meta.상태.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {/* 확인자에게는 상태 고르개보다 «됐다/아니다» 두 단추가 맞다 — 고르개를
                  뒤져 완료를 찾게 하면 검토가 밀린다. 누르면 그대로 완료로 닫힌다. */}
              {검토차례(meta, i) && (
                <div className="row" style={{ gap: 3, justifyContent: 'center', marginTop: 3 }}>
                  <button type="button" className="btn tiny" onClick={() => onStatus(i, '완료')}>
                    검토 완료
                  </button>
                  <button type="button" className="btn tiny secondary"
                    onClick={() => onStatus(i, '진행중')}>돌려보내기</button>
                </div>
              )}
            </td>
            <td className="c">
              {/* 숫자만 있으면 옆줄과 견줘지지 않는다. 길이를 같이 둬야 훑어진다. */}
              <div className="row" style={{ gap: 5, flexWrap: 'nowrap', justifyContent: 'center' }}>
                <span style={{ display: 'inline-block', width: 46, height: 6, borderRadius: 3,
                               background: '#eceff3', flex: 'none' }}>
                  <span style={{ display: 'block', width: `${i.진행률 || 0}%`, height: '100%',
                                 borderRadius: 3, background: 상태색[i.상태] || 상태색.진행중 }} />
                </span>
                <select className="w-pct" value={i.진행률 || 0}
                  disabled={['완료', '취소'].includes(i.상태) || !고칠수있나(meta, i)}
                  onChange={(e) => onProgress(i, Number(e.target.value))}>
                  {(PCT.includes(i.진행률 || 0) ? PCT : [...PCT, i.진행률].sort((a, b) => a - b))
                    .map((v) => <option key={v} value={v}>{v}%</option>)}
                </select>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}


// 같은 업무를 주간·월간으로 바꿔 본다. 마감일이 없는 업무는 달력에 놓을 자리가 없으므로
// 아래에 따로 모은다 — 안 그러면 달력에서 사라진 채 잊힌다.
// 목록에서는 색 옆에 회사 이름이 적히지만 달력 칸에는 업무명만 들어간다.
// 색이 혼자 뜻을 지게 두지 않는다 — 아래에 이름을 적어 둔다.
function 사업부범례({ meta }) {
  return (
    <div className="row" style={{ gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
      {(meta.사업부 || []).map((sg) => (
        <span key={sg.code} className="muted" style={{ fontSize: 11 }}>
          <span style={{ display: 'inline-block', width: 3, height: 10, borderRadius: 2,
                         background: 사업부색[sg.code] || '#cbd5e1', marginRight: 5,
                         verticalAlign: 'middle' }} />{sg.name}
        </span>
      ))}
      {/* 흐린 칸이 무엇인지 적어 두지 않으면 «왜 어떤 건 연하지» 로 남는다 */}
      <span style={{ fontSize: 11, color: '#8a93a0', border: '1px dashed #d7dde6',
                     borderRadius: 3, padding: '0 5px' }}>
        흐린 칸 = 아직 만들지 않은 예정 (마감 일주일 전에 업무로 생깁니다)
      </span>
    </div>
  )
}

function 미지정목록({ rows, onOpen }) {
  const 없는것 = rows.filter((i) => !i.마감일)
  if (!없는것.length) return null
  return (
    <div style={{ marginTop: 10, borderTop: '1px dashed #e5e9ef', paddingTop: 8 }}>
      <b className="muted" style={{ fontSize: 11 }}>일정 미지정 {없는것.length}건 — 달력에 자리가 없어 여기 둡니다</b>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
        {없는것.map((i) => (
          <button key={i.id} type="button" className="data-tab" onClick={() => onOpen(i.id)}>
            {i.업무명} <span className="muted">· {i.담당자명}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function 칸({ i, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(i.id)}
      title={`${i.업무명} · ${i.담당자명} · ${i.상태}`}
      data-seg={i.사업부}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', border: 0,
        // 목록의 사업부 띠와 같은 색이라, 달력에서도 어느 회사 일인지 바로 읽힌다.
        borderLeft: `3px solid ${사업부색[i.사업부] || '#cbd5e1'}`, borderRadius: 3,
        padding: '1px 4px', marginBottom: 1, fontSize: 10.5,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        background: i.지연여부 ? '#fdecea' : ['완료', '취소'].includes(i.상태) ? '#eef7ee' : '#f4f6fa',
        color: i.지연여부 ? '#b3261e' : '#333',
        textDecoration: i.상태 === '취소' ? 'line-through' : 'none',
      }}>{i.업무명}</button>
  )
}

// 아직 만들지 않은 회차. 흐리고 테두리가 점선이라 «있는 일»과 한눈에 갈린다.
// 누를 수 없다 — 아직 건이 없어 열 것이 없고, 눌리면 사라진 업무처럼 보인다.
function 예정칸({ i }) {
  return (
    <div title={`${i.업무명} · ${i.담당자명 || ''} · ${i.주기} · 아직 만들지 않은 예정`}
      style={{
        borderLeft: `3px solid ${사업부색[i.사업부] || '#cbd5e1'}`,
        border: '1px dashed #d7dde6', borderLeftWidth: 3, borderLeftStyle: 'solid',
        borderRadius: 3, padding: '1px 4px', marginBottom: 1, fontSize: 10.5,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        background: 'transparent', color: '#8a93a0',
      }}>{i.업무명}</div>
  )
}

// 달력이 보고 있는 기간의 «예정»을 받아 온다. 목록과 같은 조건을 그대로 넘겨서,
// 걸러 놓은 조건이 달력에서만 풀리는 일이 없게 한다.
function use예정(질의, start, end) {
  const [예정, set예정] = useState([])
  useEffect(() => {
    let 살아있음 = true
    const p = new URLSearchParams(질의)
    p.set('start', start); p.set('end', end)
    portal.get(`/api/work/planned?${p}`)
      .then((r) => { if (살아있음) set예정(Array.isArray(r) ? r : []) })
      .catch(() => { if (살아있음) set예정([]) })
    return () => { 살아있음 = false }
  }, [질의, start, end])
  return 예정
}

function 주간표({ rows, meta, onOpen, 질의 }) {
  const [기준, set기준] = useState(meta.오늘)
  const 주 = useMemo(() => {
    const d = new Date(기준 + 'T00:00:00')
    const 일요일 = new Date(d); 일요일.setDate(d.getDate() - d.getDay())
    return Array.from({ length: 7 }, (_, n) => {
      const x = new Date(일요일); x.setDate(일요일.getDate() + n)
      const iso = `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
      return { iso, 요일: WEEK_HEAD[n], 일: x.getDate() }
    })
  }, [기준])
  const 옮기기 = (n) => {
    const d = new Date(기준 + 'T00:00:00'); d.setDate(d.getDate() + n)
    set기준(d.toISOString().slice(0, 10))
  }
  const 예정 = use예정(질의, 주[0].iso, 주[6].iso)
  return (
    <>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <button type="button" className="btn tiny secondary" onClick={() => 옮기기(-7)}>← 지난주</button>
        <b>{주[0].iso} ~ {주[6].iso}</b>
        <button type="button" className="btn tiny secondary" onClick={() => 옮기기(7)}>다음주 →</button>
        <button type="button" className="link-cell" onClick={() => set기준(meta.오늘)}>이번주</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {주.map((d) => {
          const 건 = rows.filter((i) => i.마감일 === d.iso)
          const 올것 = 예정.filter((i) => i.마감일 === d.iso)
          return (
            <div key={d.iso} style={{
              minHeight: 110, border: '1px solid #eceff3', borderRadius: 4, padding: 4,
              background: d.iso === meta.오늘 ? '#f2f5ff' : '#fff',
            }}>
              <div style={{ fontSize: 11, marginBottom: 3, fontWeight: d.iso === meta.오늘 ? 700 : 400 }}>
                {d.일}({d.요일})
              </div>
              {건.map((i) => <칸 key={i.id} i={i} onOpen={onOpen} />)}
              {올것.map((i) => <예정칸 key={`p${i.template_id}-${i.시작일}`} i={i} />)}
              {!건.length && !올것.length && <span className="muted" style={{ fontSize: 10 }}>—</span>}
            </div>
          )
        })}
      </div>
      <미지정목록 rows={rows} onOpen={onOpen} />
    </>
  )
}

function 월간표({ rows, meta, onOpen, 질의 }) {
  const [월, set월] = useState(meta.오늘.slice(0, 7))
  const grid = useMemo(() => {
    const [y, m] = 월.split('-').map(Number)
    const first = new Date(y, m - 1, 1)
    const 앞 = first.getDay()
    const 말일 = new Date(y, m, 0).getDate()
    const 주수 = Math.ceil((앞 + 말일) / 7)
    return Array.from({ length: 주수 * 7 }, (_, n) => {
      const d = new Date(y, m - 1, 1 - 앞 + n)
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return { iso, 일: d.getDate(), 이번달: d.getMonth() === m - 1 }
    })
  }, [월])
  const 옮기기 = (n) => {
    const [y, m] = 월.split('-').map(Number)
    const d = new Date(y, m - 1 + n, 1)
    set월(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  // 앞뒤 달 칸까지 격자에 들어오므로 격자의 첫날~마지막날로 받는다
  const 예정 = use예정(질의, grid[0].iso, grid[grid.length - 1].iso)
  return (
    <>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <button type="button" className="btn tiny secondary" onClick={() => 옮기기(-1)}>← 이전달</button>
        <b>{월}</b>
        <button type="button" className="btn tiny secondary" onClick={() => 옮기기(1)}>다음달 →</button>
        <span className="muted" style={{ fontSize: 11 }}>마감일 기준 · 왼쪽 색이 사업부</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {WEEK_HEAD.map((w) => (
          <div key={w} className="muted" style={{ textAlign: 'center', fontSize: 11 }}>{w}</div>
        ))}
        {grid.map((d) => {
          const 건 = rows.filter((i) => i.마감일 === d.iso)
          const 올것 = 예정.filter((i) => i.마감일 === d.iso)
          return (
            <div key={d.iso} style={{
              minHeight: 74, border: '1px solid #eceff3', borderRadius: 4, padding: 3,
              background: d.iso === meta.오늘 ? '#f2f5ff' : d.이번달 ? '#fff' : '#fafbfc',
              opacity: d.이번달 ? 1 : 0.55,
            }}>
              <div style={{ fontSize: 11, marginBottom: 2, fontWeight: d.iso === meta.오늘 ? 700 : 400 }}>{d.일}</div>
              {건.slice(0, 3).map((i) => <칸 key={i.id} i={i} onOpen={onOpen} />)}
              {올것.slice(0, Math.max(0, 3 - 건.length)).map((i) => (
                <예정칸 key={`p${i.template_id}-${i.시작일}`} i={i} />
              ))}
              {건.length + 올것.length > 3 && (
                <div className="muted" style={{ fontSize: 10 }}>+{건.length + 올것.length - 3}건</div>
              )}
            </div>
          )
        })}
      </div>
      <미지정목록 rows={rows} onOpen={onOpen} />
    </>
  )
}

// 달력 한 칸에 들어가는 업무. 왼쪽 띠는 사업부, 동그라미는 상태다 — 좁은 칸이라
// 글자로 다 적을 수 없고, 색만 쓰면 무슨 뜻인지 모르므로 아래에 범례를 둔다.
// 손을 올리면 사업부·상태·진행률·마감이 모두 뜬다.
function 일지칸({ i, meta, 날짜 }) {
  return (
    <div title={`${사업부이름(meta, i.사업부)} · ${i.업무명} · ${i.상태}`
      + (i.진행률 ? ` ${i.진행률}%` : '') + (i.마감일 ? ` · 마감 ${i.마감일}` : '')}
      style={{
        borderLeft: `3px solid ${사업부색[i.사업부] || '#cbd5e1'}`,
        borderRadius: 3, padding: '1px 5px', marginBottom: 2, fontSize: 10.5,
        background: '#f6f8fb', color: '#333', maxWidth: '100%',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3,
                     background: 상태색[i.상태] || '#94a3b8', marginRight: 4 }} />
      {날짜 && i.마감일 && (
        <span className="muted" style={{ marginRight: 4 }}>{i.마감일.slice(5)}</span>
      )}
      {i.업무명}
      {i.상태 === '진행중' && i.진행률 ? (
        <span className="muted"> {i.진행률}%</span>
      ) : null}
    </div>
  )
}

function JournalTab({ meta, reload, 목적지, on목적지도착 }) {
  const [day, setDay] = useState(meta.오늘)
  const [who, setWho] = useState(meta.나.id)
  const [댓글, set댓글] = useState('')
  const [댓글오류, set댓글오류] = useState('')
  useEffect(() => {
    if (!목적지) return
    if (목적지.day) setDay(목적지.day)
    if (목적지.who) setWho(목적지.who)
    setMode('day')
    on목적지도착?.()
  }, [목적지])
  const [mode, setMode] = useState('day')
  const [data, setData] = useState(null)
  const [note, setNote] = useState('')
  const [ask, setAsk] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const [status, setStatus] = useState(null)
  const load = useCallback(() => {
    setErr(''); setDone(false)
    if (mode !== 'day') return
    if (meta.팀장) {
      portal.get(`/api/work/journal-status?day=${day}`).then(setStatus).catch(() => setStatus(null))
    }
    portal.get(`/api/work/journal?day=${day}&assignee=${who}`)
      .then((d) => {
        setData(d)
        set댓글(''); set댓글오류('')
        setNote(d.제출?.특이사항 || '')
        setAsk(d.제출?.확인요청 || '')
      })
      .catch((e) => setErr(e.message))
  }, [day, who, mode, meta.팀장])
  useEffect(() => { load() }, [load, reload])

  const mine = who === meta.나.id
  // 일지에 댓글. 팀장이 "봤다 / 이건 이렇게 하자"를 남길 자리가 없으면 그 말은
  // 메신저로 새고, 그러면 무엇을 지시받았는지 일지에 안 남는다.
  const 댓글달기 = async () => {
    const 글 = 댓글.trim()
    if (!글 || !data?.제출) return
    set댓글오류('')
    try {
      const r = await portal.post(`/api/work/journal/${data.제출.id}/comments`, { 내용: 글 })
      set댓글('')
      setData((d) => ({ ...d, 댓글: r.댓글 || d.댓글 }))
    } catch (e) { set댓글오류(`댓글을 남기지 못했습니다 — ${e.message}`) }
  }
  const submit = async () => {
    setBusy(true); setErr('')
    try {
      await portal.post('/api/work/journal', { 일자: day, 특이사항: note, 확인요청: ask })
      setDone(true); load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const 구성 = data?.초안?.구성
  const SECTIONS = [['완료', '완료'], ['진행중', '진행중'], ['대기', '대기'],
                    ['지연', '지연'], ['시작전', '시작 전'], ['향후예정', '향후 예정']]
  const 있음 = 구성 && SECTIONS.some(([k]) => (구성[k] || []).length)

  return (
    <>
      <div className="row" style={{ marginBottom: 10, gap: 8 }}>
        {[['day', '일별'], ['week', '주간']].map(([k, label]) => (
          <button key={k} type="button" className={'data-tab' + (mode === k ? ' active' : '')}
            onClick={() => setMode(k)}>{label}</button>
        ))}
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        {meta.팀장 && (
          <select value={who} onChange={(e) => setWho(Number(e.target.value))}>
            {meta.담당자.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        {mode === 'week' && <span className="muted" style={{ fontSize: 12 }}>고른 날짜가 든 주를 봅니다</span>}
        {mode === 'day' && data?.제출 && (
          <span className="muted">제출됨 · {kstDate(data.제출.제출일시)}</span>
        )}
      </div>
      {mode === 'week' && <WeekView meta={meta} who={who} day={day} />}
      {mode === 'day' && (<>
      {err && <div className="error-box">{err}</div>}
      <div className="hint" style={{ marginBottom: 10 }}>
        업무관리 데이터로 자동 생성됩니다. 직접 쓰는 건 특이사항과 팀장 확인 필요사항뿐입니다.
        제출한 일지는 여기 날짜별로 쌓이고, 팀장은 아래 제출 현황에서 팀원 일지를 열어 봅니다.
      </div>
      {meta.팀장 && status && (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
            <b>제출 현황 · {status.일자}</b>
            <span className="muted" style={{ fontSize: 12 }}>
              이름을 누르면 그 사람의 일지가 아래에 열립니다
            </span>
          </div>
          <table className="w-exc">
            <thead>
              <tr><th className="c">작성자</th><th className="c">제출</th><th className="c" style={{ width: 52 }}>댓글</th>
                <th className="l">팀장 확인 필요사항</th><th className="l">특이사항</th></tr>
            </thead>
            <tbody>
              {status.현황.map((r) => (
                <tr key={r.작성자_id} className={who === r.작성자_id ? 'on' : ''}>
                  <td className="c">
                    <button type="button" className="link-cell" onClick={() => setWho(r.작성자_id)}>
                      {r.작성자명}
                    </button>
                  </td>
                  <td className="c">
                    {r.제출 ? <span className="muted">{kstDate(r.제출일시).slice(11)}</span>
                      : <span className="w-late">미제출</span>}
                  </td>
                  <td className="c">{r.댓글수 ? r.댓글수 : <span className="muted">—</span>}</td>
                  <td className="l">{r.확인요청}</td>
                  <td className="l muted">{r.특이사항}</td>
                </tr>
              ))}
              {!status.현황.length && (
                <tr><td colSpan={5} className="c muted">업무관리 권한을 가진 사람이 없습니다</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>{day} 업무일지 · {data?.초안 ? (meta.담당자.find((u) => u.id === who)?.name || '') : ''}</h3>
        {!있음 && <div className="muted">해당 날짜에 해당하는 업무가 없습니다.</div>}

        {/* 일지를 쓰는 사람이 실제로 적는 것은 셋이다 — 오늘 뭘 했고, 이번 주에 뭐가
            남았고, 다음 주에 뭐가 오는가. 아래 상태별 묶음은 «무엇이 어떤 상태인가»라
            그 셋을 사람이 머릿속에서 다시 갈라야 했다. 사업부도 같이 적는다 — 네 회사
            일을 한 사람이 같이 하므로 업무명만으로는 어느 회사 것인지 모른다. */}
        {/* 이번 주 할 일만 달력으로 편다 — «언제까지»가 요일로 보여야 주간 계획이 선다.
            금일·차주는 목록 그대로다. 하루치와 다음 주는 날짜보다 무엇인지가 먼저다. */}
        {(() => {
          const 주 = data?.초안?.주
          const items = data?.초안?.이번주할일 || []
          if (!주) return null
          const 칸 = Array.from({ length: 7 }, (_, n) => {
            const d = new Date(주.시작 + 'T00:00:00'); d.setDate(d.getDate() + n)
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            return { iso, 일: d.getDate(), 요일: WEEK_HEAD[n] }
          })
          // 마감이 이번 주보다 앞선 것(늦은 일)과 마감이 없는 것은 달력에 놓을 자리가
          // 없다. 빼면 그대로 사라지므로 달력 위에 따로 세운다.
          const 자리없음 = items.filter((i) => !i.마감일 || i.마감일 < 주.시작)
          return (
            <div style={{ marginBottom: 14 }}>
              <b>이번 주 내로 할 일</b>
              <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                {items.length}건 · {주.시작} ~ {주.끝}
              </span>
              {자리없음.length > 0 && (
                <div style={{ marginTop: 4, padding: '6px 9px', borderRadius: 5,
                              border: `1px solid ${상태색.지연}`, background: '#fdf3f3' }}>
                  <b style={{ fontSize: 12, color: 상태색.지연 }}>
                    마감이 지난 것 {자리없음.length}건
                  </b>
                  <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                    달력에 놓을 자리가 없어 여기 둡니다 — 이번 주에 끝내야 하는 일입니다
                  </span>
                  <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    {자리없음.map((i) => <일지칸 key={i.id} i={i} meta={meta} 날짜 />)}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3,
                            marginTop: 5 }}>
                {칸.map((d) => {
                  const 그날 = items.filter((i) => i.마감일 === d.iso)
                  return (
                    <div key={d.iso} style={{
                      minHeight: 86, border: '1px solid #eceff3', borderRadius: 4, padding: 4,
                      background: d.iso === meta.오늘 ? '#f2f5ff' : '#fff',
                    }}>
                      <div style={{ fontSize: 11, marginBottom: 3,
                                    fontWeight: d.iso === meta.오늘 ? 700 : 400 }}>
                        {d.일}({d.요일})
                      </div>
                      {그날.map((i) => <일지칸 key={i.id} i={i} meta={meta} />)}
                      {!그날.length && <span className="muted" style={{ fontSize: 10 }}>—</span>}
                    </div>
                  )
                })}
              </div>
              <div className="row" style={{ gap: 9, marginTop: 5, flexWrap: 'wrap' }}>
                {['시작전', '진행중', '대기', '검토대기'].map((s) => (
                  <span key={s} className="muted" style={{ fontSize: 11 }}>
                    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4,
                                   background: 상태색[s], marginRight: 4 }} />{s}
                  </span>
                ))}
                <span className="muted" style={{ fontSize: 11 }}>왼쪽 띠는 사업부</span>
              </div>
            </div>
          )
        })()}

        {[['오늘한일', '금일 한 일'],
          ['차주할일', '차주 할 일']].map(([k, label]) => {
          const items = (data?.초안 || {})[k] || []
          const 주 = data?.초안?.주
          return (
            <div key={k} style={{ marginBottom: 12 }}>
              <b>{label}</b>
              <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                {items.length}건
                {k === '차주할일' && 주 && ` · ${주.다음시작} ~ ${주.다음끝}`}
              </span>
              {!items.length
                ? <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>—</div>
                : (
                  <ul className="w-journal">
                    {items.map((i) => (
                      <li key={i.id}>
                        <span style={{ display: 'inline-block', width: 3, height: 10,
                                       borderRadius: 2, marginRight: 5, verticalAlign: 'middle',
                                       background: 사업부색[i.사업부] || '#cbd5e1' }} />
                        <span className="muted" style={{ fontSize: 11, marginRight: 5 }}>
                          {사업부이름(meta, i.사업부)}
                        </span>
                        {i.업무명}
                        <span className="muted" style={{ fontSize: 11 }}>
                          {' · '}{i.상태}
                          {i.상태 !== '완료' && i.진행률 ? ` ${i.진행률}%` : ''}
                          {i.마감일 ? ` · 마감 ${i.마감일}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          )
        })}

        <div className="hint" style={{ margin: '10px 0 6px' }}>아래는 상태별로 다시 본 것입니다</div>
        {SECTIONS.map(([k, label]) => {
          const items = (구성 || {})[k] || []
          if (!items.length) return null
          return (
            <div key={k} style={{ marginBottom: 12 }}>
              <b>{label}</b>
              <ul className="w-journal">
                {items.map((i) => (
                  <li key={i.id}>
                    <span style={{ display: 'inline-block', width: 3, height: 10, borderRadius: 2,
                                   marginRight: 5, verticalAlign: 'middle',
                                   background: 사업부색[i.사업부] || '#cbd5e1' }} />
                    <span className="muted" style={{ fontSize: 11, marginRight: 5 }}>
                      {사업부이름(meta, i.사업부)}
                    </span>
                    {i.업무명}
                    {k === '진행중' && <span className="muted"> / {i.진행률}%</span>}
                    {k === '향후예정' && <span className="muted"> / {i.마감일}</span>}
                    {k === '대기' && (
                      <ul>
                        {i.대기사유 && <li className="muted">{i.대기사유}</li>}
                        {i.예상완료일 && <li className="muted">예상 완료일: {i.예상완료일}</li>}
                      </ul>
                    )}
                    {k === '지연' && (
                      <ul><li className="muted">기존 마감일: {i.마감일}</li></ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )
        })}

        <div style={{ marginTop: 14 }}>
          <b>특이사항</b>
          <textarea rows={3} value={note} disabled={!mine} style={{ width: '100%', marginTop: 4 }}
            onChange={(e) => setNote(e.target.value)} />
        </div>
        <div style={{ marginTop: 10 }}>
          <b>팀장 확인 필요사항</b>
          <textarea rows={3} value={ask} disabled={!mine} style={{ width: '100%', marginTop: 4 }}
            onChange={(e) => setAsk(e.target.value)} />
        </div>
        {mine && (
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            {done && <span className="muted" style={{ marginRight: 10 }}>제출했습니다</span>}
            <button type="button" className="btn" disabled={busy} onClick={submit}>
              {busy ? '제출 중…' : data?.제출 ? '다시 제출' : '오늘 업무 마감'}
            </button>
          </div>
        )}

        {/* 댓글은 제출한 일지에만 붙는다 — 아직 안 낸 것에 답을 달 수는 없다. */}
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 14, paddingTop: 10 }}>
          <b>댓글</b>
          {!data?.제출 ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              아직 제출하지 않은 일지입니다. 제출하면 댓글을 주고받을 수 있습니다.
            </div>
          ) : (
            <>
              {!(data.댓글 || []).length && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>아직 댓글이 없습니다.</div>
              )}
              {(data.댓글 || []).map((c) => (
                <div key={c.id} style={{ marginTop: 8, lineHeight: 1.5 }}>
                  <b style={{ fontSize: 12 }}>{c.작성자명}</b>
                  <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                    {kstDate(c.작성시각)}
                  </span>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{c.내용}</div>
                </div>
              ))}
              {댓글오류 && <div className="error-box" style={{ marginTop: 8 }}>{댓글오류}</div>}
              <div className="row" style={{ gap: 6, marginTop: 10, alignItems: 'flex-start' }}>
                <textarea rows={2} value={댓글} style={{ flex: 1 }}
                  placeholder={meta.팀장 && !mine
                    ? '예: 확인했습니다 / 링크맘 건은 세무사 회신 먼저 받아 주세요'
                    : '답글을 남깁니다'}
                  onChange={(e) => set댓글(e.target.value)} />
                <button type="button" className="btn" disabled={!댓글.trim()} onClick={댓글달기}>
                  남기기
                </button>
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                남기면 일지를 쓴 사람과 먼저 댓글을 단 사람에게 🔔 알림이 갑니다 (본인 제외).
              </div>
            </>
          )}
        </div>
      </div>
      </>)}
    </>
  )
}

export default function Work({ month, autoOpenWorkId, onWorkOpened,
                              autoOpenJournal, onJournalOpened }) {
  const [tab, setTab] = useState('main')
  const [open, setOpen] = useState(null)   // 상세 페이지로 연 업무
  const [meta, setMeta] = useState(null)
  const [err, setErr] = useState('')
  const [tick, setTick] = useState(0)
  const bump = () => setTick((t) => t + 1)

  useEffect(() => {
    portal.get('/api/work/meta').then(setMeta).catch((e) => setErr(e.message))
  }, [])

  // 종에서 넘어왔으면 그 업무를 바로 편다. 목록에서 다시 찾게 하면 알림이 반쪽이 된다.
  useEffect(() => {
    if (!autoOpenWorkId) return
    setOpen(autoOpenWorkId)
    onWorkOpened?.()
  }, [autoOpenWorkId])

  // 일지 댓글 알림은 그날 그 사람의 일지로 보낸다 — 날짜를 직접 맞추게 하면 안 본다.
  const [일지목적지, set일지목적지] = useState(null)
  const [편집, set편집] = useState(null)
  useEffect(() => {
    if (!autoOpenJournal) return
    setTab('journal')
    set일지목적지(autoOpenJournal)
    onJournalOpened?.()
  }, [autoOpenJournal])

  if (err) return <><h2>업무관리</h2><div className="error-box">{err}</div></>
  if (!meta) return <><h2>업무관리</h2><div className="muted">불러오는 중…</div></>

  return (
    <div className="work-dense">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2>업무관리</h2>
        {meta.팀장 && <span className="muted">팀장 권한</span>}
      </div>
      <div className="data-tabs" style={{ marginBottom: 12 }}>
        {TABS.filter((t) => !t.lead || meta.팀장).map((t) => (
          <button key={t.k} type="button"
            className={'data-tab' + (tab === t.k ? ' active' : '')}
            onClick={() => setTab(t.k)}>{t.label}</button>
        ))}
      </div>
      {tab === 'main' && <MainTab meta={meta} reload={tick} bump={bump}
        onOpen={setOpen} openId={open} />}
      {tab === 'dash' && meta.팀장 && <DashboardTab meta={meta} month={month} onOpen={setOpen} />}
      {tab === 'journal' && <JournalTab meta={meta} reload={tick}
        목적지={일지목적지} on목적지도착={() => set일지목적지(null)} />}

      {/* 상세는 팝업으로 띄운다. 화면을 갈아 끼우면 어디까지 보고 있었는지,
          어떤 조건을 걸어 놨었는지가 닫을 때마다 사라진다 — 목록은 뒤에 그대로 둔다. */}
      {open && (
        <div className="modal-backdrop" onMouseDown={() => { setOpen(null); bump() }}>
          <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()}
            style={{ width: 920 }}>
            <div className="modal-header">
              <h3>업무 상세</h3>
              <button type="button" className="modal-close"
                onClick={() => { setOpen(null); bump() }}>×</button>
            </div>
            <div className="modal-body">
              <DetailPage id={open} meta={meta}
                onClose={() => { setOpen(null); bump() }} onChanged={bump}
                onEdit={set편집} />
            </div>
          </div>
        </div>
      )}
      {/* 상세 뒤에 그린다 — z-index 가 같으므로 나중에 그린 것이 위에 온다.
          앞에 두면 수정 창이 상세 창에 가려 안 보인다. */}
      {편집 && (
        <WorkModal meta={meta} item={편집} onClose={() => set편집(null)}
          onSaved={() => { set편집(null); bump() }} />
      )}
    </div>
  )
}
